import { engagementTemplate, bindEngagement } from "./engagement";
import { managedAppsTemplate, bindManagedApps, type ManagedAppView } from "./managed-apps";
import { appContentTemplate, bindAppContent, type AppContentPage } from "./app-content";
import {
  createAccountCrypto,
  decryptVaultValue,
  derivePasswordKeys,
  encryptVaultValue,
  makeRecoveryKit,
  unlockEnvelopes,
  type AccountCrypto,
} from "./crypto";
import {
  randomBase64Url,
  textEncoder,
  toBase64Url,
  type KdfParameters,
  type SignedDescriptorEvent,
  type WrappedEnvelope,
} from "../shared/protocol";
import { POST_TEMPLATE_ID, type AppOnboardingFieldManifest } from "../shared/apps";
import {
  emailVerificationRequestSigningInput,
  type EmailVerificationRequestV1,
  type VerifiedEmailCredentialV1,
} from "../shared/attester";
import {
  normalizeSelfIssuedNameClaim,
  SELF_ISSUED_CREDENTIAL_TYPE,
  selfIssuedCredentialSigningInput,
  type SelfIssuedCredentialV1,
} from "../shared/credentials";
import {
  readBrowserIdentities,
  rememberBrowserIdentity,
  requireExpectedRootDid,
  type BrowserIdentity,
} from "../web-shared/browser-identities";
import {
  avatar,
  dashboardShellTemplate,
  guidedStepsTemplate,
  icon,
  identicon,
  identitySwitcherTemplate,
  normalizeProfileImage,
  type IdentityView,
  type UiIconName,
} from "../web-shared/ui";

interface ProfileView extends IdentityView {
  coverDataUri: string | null;
  profileUpdatedAt: string | null;
}

interface SessionView {
  authenticated: boolean;
  csrfToken?: string;
  profile?: ProfileView;
  recoveryReady?: boolean;
}

interface DashboardView {
  operator: boolean;
  managedApps: ManagedAppView[];
  profile: ProfileView;
  privateAccount: { email: string | null };
  credentials: Array<{
    id: string;
    type: string;
    issuerDid: string;
    claims: Record<string, string>;
    issuedAt: string;
    expiresAt: string | null;
    verification: "self_signed" | "issuer_signed";
  }>;
  credentialIssuers: { verifiedEmailAvailable: boolean };
  recoveryReady: boolean;
  grants: Array<{ id: string; clientId: string; scopes: string[]; createdAt: string; lastUsedAt: string }>;
  sessions: Array<{ label: string; created_at: string; last_seen_at: string; expires_at: string; current: boolean }>;
  csrfToken: string;
  services: ServiceView[];
  googleCalendar: GoogleCalendarStatusView;
  googleCalendars: GoogleCalendarView[];
  availableApps: Array<{ name: string; origin: string }>;
}

interface GoogleCalendarStatusView {
  service: "google.calendar";
  configured: boolean;
  credentialStorageAvailable: boolean;
  connected: boolean;
  needsReconnect: boolean;
  selectedCalendarCount: number;
  calendarCount: number;
  eventCount: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

interface GoogleCalendarView {
  id: string;
  externalId: string;
  summary: string;
  description: string | null;
  timeZone: string | null;
  primary: boolean;
  included: boolean;
}

interface ServiceView {
  service: "openai.embeddings";
  active: boolean;
  available: boolean;
  provider: "openai";
  model: string;
  dimensions: number;
  indexed: number;
  pending: number;
  failed: number;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
}

interface UnlockedState {
  privateJwk: JsonWebKey;
  dek: Uint8Array;
  rootDid: string;
  descriptor: SignedDescriptorEvent;
}

interface AuthorizationContext {
  authenticated: boolean;
  csrfToken?: string;
  profile?: { handle: string; displayName: string; rootDid: string };
  app: {
    origin: string;
    hostname: string;
    name: string;
    manifestVerified: boolean;
    presentation?: { tagline: string; description: string; logo?: string; heroImage?: string; locale?: string; accentColor?: string };
  };
  requestedScopes: string[];
  alreadyGranted?: boolean;
  onboarding?: {
    title: string;
    description: string;
    fields: AppOnboardingFieldManifest[];
    values: Record<string, string>;
    complete: boolean;
  };
  credentialRequirements?: CredentialRequirementView[];
  credentialIssuers?: { verifiedEmailAvailable: boolean };
}

interface CredentialCandidateView {
  id: string;
  type: string;
  issuerDid: string;
  verification: "self_signed" | "issuer_signed";
  disclosedClaims: Record<string, string>;
}

interface CredentialRequirementView {
  id: string;
  type: string;
  claims: string[];
  required: boolean;
  label?: string;
  purpose: string;
  candidates: CredentialCandidateView[];
  selectedCredentialId: string | null;
}

type DashboardPage = "overview" | "profile" | "identity" | "security" | "apps" | "my-apps" | "services" | "support" | "engagement" | "operator";
let authorizationAppName = "appen";
let appContentPage: AppContentPage | null = null;

const app = document.querySelector<HTMLDivElement>("#app")!;
const toastElement = document.querySelector<HTMLDivElement>("#toast")!;
const browserIdentityStorageKey = `vibe:cloud-identities:${location.origin}`;
let authMode: "login" | "signup" = "login";
let unlocked: UnlockedState | null = null;
let dashboard: DashboardView | null = null;
let pendingRecovery: Awaited<ReturnType<typeof makeRecoveryKit>> | null = null;
let setupWizard: { quest: "recovery" | "openai"; step: number } | null = null;
let setupRefreshTimer: number | null = null;
let accountMenuOpen = false;
let pendingAppOnboarding: { clientId: string; values: Record<string, string> } | null = null;
let pendingCredentialSelections: { clientId: string; values: Record<string, string> } | null = null;
let pendingEmailVerification: { verificationId: string; email: string; subjectDid: string } | null = null;

void start();

document.addEventListener("click", (event) => {
  if (!accountMenuOpen || (event.target as Element | null)?.closest(".ui-identity-switcher")) return;
  accountMenuOpen = false;
  rerenderDashboard();
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || !accountMenuOpen) return;
  accountMenuOpen = false;
  rerenderDashboard();
});

async function start(): Promise<void> {
  if (location.pathname === "/authorize") await renderAuthorization();
  else if (location.pathname === "/logout") await renderGlobalLogout();
  else await renderHome();
}

async function renderHome(): Promise<void> {
  const session = await api<SessionView>("/api/session", {}, true);
  if (!session?.authenticated) {
    dashboard = null;
    app.innerHTML = landingTemplate();
    bindAuthForms(false);
    return;
  }
  dashboard = await api<DashboardView>("/api/dashboard");
  appContentPage = null;
  if (location.pathname === "/app-content") {
    const query = new URLSearchParams(location.search);
    const managedApp = dashboard.managedApps.find(item => item.id === query.get("app") && item.contentTypes.length);
    if (managedApp) appContentPage = await api<AppContentPage>(`/api/managed-apps/${encodeURIComponent(managedApp.id)}/content?${new URLSearchParams({ offset: query.get("offset") ?? "0", templateId: query.get("type") ?? managedApp.contentTypes[0]!.id })}`);
  }
  try {
    rememberCloudIdentity(dashboard.profile);
  } catch (error) {
    try {
      await api("/api/auth/logout", { method: "POST", headers: { "X-CSRF-Token": dashboard.csrfToken } });
    } catch { /* the local continuity block still wins if logout confirmation fails */ }
    dashboard = null;
    app.innerHTML = landingTemplate();
    bindAuthForms(false);
    toast(messageOf(error));
    return;
  }
  app.innerHTML = dashboardTemplate(dashboard, dashboardPageFromPath(location.pathname));
  bindDashboard();
}

function landingTemplate(): string {
  return `
    <div class="shell">
      <main class="page landing-page">
        <div class="landing-brand"><img src="/assets/vibe-cloud.png" alt="" /><strong>Vibe Cloud</strong></div>
        <div class="hero">
          <section>
            <div class="eyebrow">A personal server with a front door</div>
            <h1>Your identity.<br>Your apps.<br>Your node.</h1>
            <p class="lede">Reach your digital life from any browser, while keeping the keys that matter on your side of the glass.</p>
            <div class="trust-list">
              <div class="trust-item"><span class="trust-icon">${icon("check", { size: 16 })}</span><span>Your identity keys and sealed drawer are encrypted in this browser.</span></div>
              <div class="trust-item"><span class="trust-icon">${icon("check", { size: 16 })}</span><span>Connected apps receive only the access you approve.</span></div>
              <div class="trust-item"><span class="trust-icon">${icon("check", { size: 16 })}</span><span>App data is host-readable so apps can work. Vibe says that plainly.</span></div>
            </div>
          </section>
          <section class="card auth-card">${authTemplate()}</section>
        </div>
      </main>
    </div>`;
}

function authTemplate(compact = false, loginHint = "", swedish = false): string {
  if (swedish) return `
    <div class="tabs" role="tablist"><button class="tab ${authMode === "login" ? "active" : ""}" data-auth-tab="login" type="button">Logga in</button><button class="tab ${authMode === "signup" ? "active" : ""}" data-auth-tab="signup" type="button">Skapa konto</button></div>
    <form id="auth-form">
      ${authMode === "signup" ? `<div class="field"><label for="display-name">Ditt namn</label><input id="display-name" name="displayName" autocomplete="name" maxlength="80" required /></div><div class="field"><label for="handle">Användarnamn</label><input id="handle" name="handle" autocomplete="username" pattern="[a-z][a-z0-9\\-]{2,30}" required /><p class="field-note">3–31 små bokstäver, siffror eller bindestreck. Börja med en bokstav.</p></div>` : ""}
      <div class="field"><label for="identifier">${authMode === "signup" ? "E-post (valfritt)" : "E-post eller användarnamn"}</label><input id="identifier" name="identifier" type="${authMode === "signup" ? "email" : "text"}" autocomplete="username" ${authMode === "login" ? "required" : ""} value="${authMode === "login" ? h(loginHint) : ""}" /></div>
      <div class="field"><label for="password">Lösenord</label><input id="password" name="password" type="password" autocomplete="${authMode === "signup" ? "new-password" : "current-password"}" minlength="10" required /></div>
      ${authMode === "signup" ? `<p class="hint">Använd minst 10 tecken. Kontot skapas hos Vibe och kan också användas i andra Vibe-appar.</p>` : ""}
      <p id="auth-error" class="error" hidden></p><button class="button full" type="submit">${authMode === "signup" ? "Skapa konto och fortsätt" : `Logga in på ${h(authorizationAppName)}`}</button>
      <p class="security-note">Ditt lösenord hanteras av Vibe och delas aldrig med ${h(authorizationAppName)}.</p>
    </form>`;
  return `
    <div class="tabs" role="tablist">
      <button class="tab ${authMode === "login" ? "active" : ""}" data-auth-tab="login" type="button">Sign in</button>
      <button class="tab ${authMode === "signup" ? "active" : ""}" data-auth-tab="signup" type="button">Create account</button>
    </div>
    <form id="auth-form">
      ${authMode === "signup" ? `<div class="field"><label for="display-name">Your name</label><input id="display-name" name="displayName" autocomplete="name" maxlength="80" required placeholder="Patrik" /></div>` : ""}
      ${authMode === "signup" ? `<div class="field"><label for="handle">Handle</label><input id="handle" name="handle" autocomplete="username" pattern="[a-z][a-z0-9\\-]{2,30}" required placeholder="patrik" /><p class="field-note">Your public Vibe name. It stays stable if your email changes.</p></div>` : ""}
      <div class="field"><label for="identifier">${authMode === "signup" ? "Email (optional)" : "Email or handle"}</label><input id="identifier" name="identifier" type="${authMode === "signup" ? "email" : "text"}" autocomplete="username" ${authMode === "login" ? "required" : ""} value="${authMode === "login" ? h(loginHint) : ""}" placeholder="${authMode === "signup" ? "you@example.com" : "you@example.com or patrik"}" />${authMode === "signup" ? `<p class="field-note">A private sign-in email that you can change later.</p>` : ""}</div>
      <div class="field"><label for="password">Password</label><input id="password" name="password" type="password" autocomplete="${authMode === "signup" ? "new-password" : "current-password"}" minlength="10" required /></div>
      ${authMode === "signup" ? `<p class="hint">Use at least 10 characters. Your password never leaves this page; the browser sends a separate derived login secret.</p>` : ""}
      ${authMode === "login" ? `<p class="hint legacy-login-hint">Use the email address or handle for your Vibe account.</p>` : ""}
      <p id="auth-error" class="error" hidden></p>
      <button class="button full" type="submit">${authMode === "signup" ? "Create Vibe account" : "Sign in"}</button>
      ${compact ? `<p class="security-note">Securely handled by Vibe. Your password is never shared with the app.</p>` : ""}
    </form>`;
}

function dashboardTemplate(view: DashboardView, page: DashboardPage): string {
  return dashboardShellTemplate({
    className: "shell admin-shell",
    sidebar: adminSidebarTemplate(view, page),
    main: `<main class="admin-main">
      <div class="admin-content">
        ${dashboardPageTemplate(view, page)}
      </div>
    </main>`,
    includeModalRoot: true,
  });
}

function adminSidebarTemplate(view: DashboardView, page: DashboardPage): string {
  const remembered = rememberedCloudIdentities().filter((identity) => identity.rootDid !== view.profile.rootDid);
  return `<aside class="admin-sidebar">
    <div class="admin-brand"><img src="/assets/vibe-cloud.png" alt="" /><strong>Vibe Cloud</strong></div>
    <nav class="admin-nav" aria-label="Node sections">
      <div class="admin-nav-label">Workspace</div>
      ${adminNavLink("/overview", "overview", "house", "Overview", page)}
      ${adminNavLink("/profile", "profile", "user-round", "Profile", page)}
      ${adminNavLink("/identity", "identity", "fingerprint", "Identity", page)}
      ${adminNavLink("/security", "security", "shield-check", "Security", page)}
      <div class="admin-nav-label separated">Platform</div>
      ${adminNavLink("/apps", "apps", "layout-grid", "Connected apps", page)}
      ${adminNavLink("/my-apps", "my-apps", "database", "Mina appar", page)}
      ${adminNavLink("/services", "services", "sparkles", "Services", page)}
      ${adminNavLink("/support", "support", "user-round", "Support", page)}
      ${view.managedApps.length ? adminNavLink("/engagement", "engagement", "layout-grid", "Engagemang", page) : ""}
      ${view.operator ? adminNavLink("/operator", "operator", "database", "Instansöversikt", page) : ""}
    </nav>
    <div class="admin-sidebar-footer">${identitySwitcherTemplate({
      profile: view.profile,
      open: accountMenuOpen,
      remembered,
      menuLabel: "Vibe Cloud identity",
      switchTitle: "Switch identity",
      switchDescription: "Sign in or create another independent identity",
      logoutTitle: "Log out of Vibe Cloud",
      logoutDescription: "End this Vibe Cloud browser session",
      profileHref: "/profile",
      privacyCopy: "Only identities used through Vibe Cloud in this browser are listed here. The list contains no email, token, or parent-account link and is never uploaded.",
    })}</div>
  </aside>`;
}

function adminNavLink(path: string, target: DashboardPage, iconName: UiIconName, label: string, page: DashboardPage): string {
  return `<a href="${path}" class="${target === page ? "active" : ""}" ${target === page ? `aria-current="page"` : ""}><span>${icon(iconName, { size: 17 })}</span>${label}</a>`;
}

function dashboardPageFromPath(pathname: string): DashboardPage {
  if (location.hash === "#profile") return "profile";
  if (location.hash === "#services") return "services";
  if (["/support", "/engagement", "/operator"].includes(pathname)) return pathname.slice(1) as DashboardPage;
  if (pathname === "/profile") return "profile";
  if (pathname === "/identity") return "identity";
  if (pathname === "/security") return "security";
  if (pathname === "/apps") return "apps";
  if (pathname === "/my-apps") return "my-apps";
  if (pathname === "/app-content") return "my-apps";
  if (pathname === "/services") return "services";
  return "overview";
}

function dashboardPageTemplate(view: DashboardView, page: DashboardPage): string {
  if (["support", "engagement", "operator"].includes(page)) return engagementTemplate();
  if (location.pathname === "/app-content") return appContentTemplate(view.managedApps.find(item => item.id === new URLSearchParams(location.search).get("app")), appContentPage);
  if (page === "my-apps") return managedAppsTemplate(view.managedApps);
  if (page === "overview") {
    return `<section class="dashboard-page">
      ${dashboardPageHeading("Overview", `Good to see you, ${firstName(view.profile.displayName)}. Here is what your apps can reach.`, "Personal node")}
      <div class="grid">${setupBoardTemplate(view)}</div>
    </section>`;
  }
  if (page === "profile") return profilePageTemplate(view);
  if (page === "identity") {
    return `<section class="dashboard-page">
      ${dashboardPageHeading("Identity", "Your stable public identity and the profile attached to this node.")}
      <div class="grid"><section class="card span-12">
        <div class="card-head"><div><h3>Root identity</h3><p>This stays the same when your node moves.</p></div><span class="status blue">P-256</span></div>
        <div class="identity-proof-row"><span class="identity-proof-identicon">${identicon(view.profile.rootDid, 48, "DID identicon")}</span><div class="did">${h(view.profile.rootDid)}</div><button id="copy-did" class="button secondary compact" type="button">${icon("copy", { size: 15 })}Copy DID</button></div>
      </section>
      <section class="card span-12 private-alias-card">
        <div class="card-head"><div><h3>Sign-in email</h3><p>A private, replaceable alias for this account. It is not shared with connected apps or added to your public identity.</p></div><span class="status blue">Private</span></div>
        <form id="email-form">
          <div class="identity-fields">
            <div class="field"><label for="account-email">Email</label><input id="account-email" name="email" type="email" autocomplete="email" required value="${h(view.privateAccount.email ?? "")}" placeholder="you@example.com" /></div>
            <div class="field"><label for="email-password">Confirm with password</label><input id="email-password" name="password" type="password" autocomplete="current-password" required /></div>
          </div>
          <p id="email-error" class="error" hidden></p>
          <div class="button-row"><button class="button" type="submit">${view.privateAccount.email ? "Change email" : "Add sign-in email"}</button><span id="email-status" class="hint"></span></div>
        </form>
      </section>
      ${verifiedEmailCredentialCard(view)}</div>
    </section>`;
  }
  if (page === "security") {
    return `<section class="dashboard-page">
      ${dashboardPageHeading("Security", "Recovery and encrypted data controls for this browser and node.")}
      <div class="grid"><section class="card span-5">
        <div class="card-head"><div><h3>Recovery</h3><p>The kit restores authority; backups restore data.</p></div><span class="status ${view.recoveryReady ? "good" : "warn"}"><span class="dot"></span>${view.recoveryReady ? "Kit ready" : "Action needed"}</span></div>
        ${view.recoveryReady ? `<p class="hint">Your node holds recovery-wrapped envelopes. Keep the downloaded kit and written code apart.</p>` : `<p class="hint">Prepare the kit before putting anything in your sealed drawer.</p>`}
        <button class="button secondary open-setup" data-setup-quest="recovery" type="button">${view.recoveryReady ? "Review recovery setup" : "Open recovery setup"}</button>
      </section>
      <section class="card span-7">
        <div class="card-head"><div><h3>Two drawers</h3><p>Different jobs, different custody.</p></div></div>
        <div class="drawer"><span class="drawer-icon">${icon("lock-keyhole", { size: 21 })}</span><div><strong>Sealed drawer</strong><p>Root keys and personal secrets. Only an unlocked browser can decrypt them.</p></div></div>
        <div style="height:10px"></div>
        <div class="drawer"><span class="drawer-icon">${icon("database", { size: 21 })}</span><div><strong>App drawer</strong><p>Profile and app-specific data. Your node reads this so connected apps can work.</p></div></div>
      </section>
      <section class="card span-12">
        <div class="card-head"><div><h3>Sealed note</h3><p>A small end-to-end encryption proof.</p></div><span class="status ${unlocked ? "good" : "warn"}">${unlocked ? "Unlocked in this tab" : "Locked"}</span></div>
        ${sealedNoteTemplate(view.recoveryReady)}
      </section></div>
    </section>`;
  }
  if (page === "apps") {
    return `<section class="dashboard-page">
      ${dashboardPageHeading("Connected apps", "Review and revoke the applications that can use this node.")}
      <div class="grid"><section class="card span-12">
        <div class="card-head"><div><h3>Connected apps</h3><p>Revocation is checked on every request.</p></div><span class="status blue">${view.grants.length} connected</span></div>
        ${view.grants.length === 0 ? `<div class="empty">No apps connected yet. Open VibeNotes to try the flow.</div>` : view.grants.map(grantTemplate).join("")}
      </section></div>
    </section>`;
  }
  return `<section class="dashboard-page">
    ${dashboardPageHeading("Services", "Node-owned integrations that apps can use without receiving your provider credentials.")}
    <div class="grid"><section class="card span-12">${googleCalendarTemplate(view.googleCalendar, view.googleCalendars)}</section><section class="card span-12">${serviceTemplate(view.services[0]!)}</section></div>
  </section>`;
}

function verifiedEmailCredentialCard(view: DashboardView): string {
  const signInEmail = view.privateAccount.email;
  const verified = view.credentials.find((credential) =>
    credential.type === "vibe.credential.verified-email.v1" &&
    credential.claims.email === signInEmail
  );
  if (verified) {
    return `<section class="card span-12 credential-console-card">
      <div class="card-head"><div><h3>Verified email credential</h3><p>An Attester has verified mailbox control for this root DID. This remains separate from the private sign-in alias.</p></div><span class="status good"><span class="dot"></span>Attested</span></div>
      <div class="credential-console-result"><div><span>Email claim</span><strong>${h(verified.claims.email ?? "")}</strong></div><div><span>Issuer</span><code>${h(shortDid(verified.issuerDid))}</code></div><div><span>Expires</span><strong>${verified.expiresAt ? h(new Date(verified.expiresAt).toLocaleDateString()) : "No expiry"}</strong></div></div>
      <p class="hint">Apps receive this claim only when their manifest declares it and you explicitly approve a presentation.</p>
    </section>`;
  }
  const unavailable = !view.credentialIssuers.verifiedEmailAvailable;
  const setup = !signInEmail
    ? `<div class="empty">Add a private sign-in email above before attesting that same address.</div>`
    : unavailable
      ? `<div class="warning-box"><strong>Attester unavailable</strong><br>The host has not configured its trusted Attester and delivery provider. The sign-in alias remains unverified and is not treated as a credential.</div>`
      : pendingEmailVerification
        ? `<form id="console-email-attestation-confirm"><div class="field"><label for="console-email-code">Code sent to ${h(pendingEmailVerification.email)}</label><input id="console-email-code" name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" required /></div><p id="console-credential-error" class="error" hidden></p><div class="button-row"><button class="button" type="submit">Verify and save credential</button><button id="cancel-console-email-attestation" class="button link" type="button">Cancel</button></div></form>`
        : `<form id="console-email-attestation-start"><input name="email" value="${h(signInEmail)}" hidden />${unlocked ? "" : `<div class="field"><label for="console-credential-password">Unlock root key with your password</label><input id="console-credential-password" name="password" type="password" autocomplete="current-password" required /></div>`}<p id="console-credential-error" class="error" hidden></p><button class="button" type="submit">${unlocked ? "Send verification code" : "Unlock and send code"}</button></form>`;
  return `<section class="card span-12 credential-console-card">
    <div class="card-head"><div><h3>Verified email credential</h3><p>Your sign-in email is currently only a private login alias. Verify it separately to create an Attester-signed claim for this root DID.</p></div><span class="status warn"><span class="dot"></span>Not attested</span></div>
    ${signInEmail ? `<div class="origin-box"><strong>${h(signInEmail)}</strong><span>Private sign-in alias · not shared or verified yet</span></div>` : ""}
    ${setup}
  </section>`;
}

function profilePageTemplate(view: DashboardView): string {
  const profile = view.profile;
  return `<section class="dashboard-page profile-page">
    ${dashboardPageHeading("Profile", "The public face of this Vibe identity. Your private sign-in email lives separately under Identity.")}
    <section class="profile-surface">
      <div class="profile-cover ${profile.coverDataUri ? "has-image" : ""}">
        ${profile.coverDataUri ? `<img src="${h(profile.coverDataUri)}" alt="" />` : `<div class="profile-cover-fallback"></div>`}
        <div class="profile-cover-actions"><button id="choose-cover" class="button secondary compact" type="button">${icon("camera", { size: 16 })}${profile.coverDataUri ? "Change cover" : "Add cover"}</button>${profile.coverDataUri ? `<button id="remove-cover" class="button secondary compact" type="button">${icon("trash", { size: 15 })}Remove</button>` : ""}</div>
        <input id="cover-input" type="file" accept="image/jpeg,image/png,image/webp" hidden />
      </div>
      <div class="profile-identity-row">
        <div class="profile-avatar-wrap">${avatar(profile, "large")}<button id="choose-avatar" class="profile-camera" type="button" aria-label="Change profile picture">${icon("camera", { size: 18 })}</button><input id="avatar-input" type="file" accept="image/jpeg,image/png,image/webp" hidden /></div>
        <div class="profile-name-block"><h2>${h(profile.displayName)}</h2><span>@${h(profile.handle)}</span><div class="profile-did-cue">${identicon(profile.rootDid, 24, "DID identicon")}<code>${h(shortDid(profile.rootDid))}</code></div></div>
      </div>
      <div class="profile-editor">
        <form id="profile-name-form"><div class="field"><label for="profile-display-name">Display name</label><input id="profile-display-name" name="displayName" maxlength="80" autocomplete="name" required value="${h(profile.displayName)}" /></div><button class="button" type="submit">Save name</button></form>
        <div class="profile-image-help"><strong>${icon("image", { size: 17 })}Profile images</strong><p>Images are cropped and reduced in this browser before upload. The profile picture is shared with apps that receive profile access; the sign-in email is not.</p>${profile.avatarDataUri ? `<button id="remove-avatar" class="button link danger-text" type="button">Remove profile picture</button>` : ""}</div>
      </div>
      <p id="profile-error" class="error profile-error" hidden></p>
    </section>
    <section class="card profile-proof-card"><div><span class="profile-proof-icon">${identicon(profile.rootDid, 46, "DID identicon")}</span><div><h3>Recognize this DID</h3><p>The deterministic pattern is a quick comparison aid for this stable DID. Cryptographic verification still depends on the signed identity data.</p></div></div><button id="copy-profile-did" class="button secondary" type="button">${icon("copy", { size: 16 })}Copy full DID</button></section>
  </section>`;
}

function dashboardPageHeading(title: string, description: string, eyebrow?: string): string {
  return `<header class="dashboard-head"><div>${eyebrow ? `<div class="eyebrow">${h(eyebrow)}</div>` : ""}<h1>${h(title)}</h1><p>${h(description)}</p></div></header>`;
}

function sealedNoteTemplate(recoveryReady: boolean): string {
  if (!unlocked) {
    return `<form id="unlock-form"><div class="field"><label for="unlock-password">Password</label><input id="unlock-password" name="password" type="password" autocomplete="current-password" required /></div><p id="unlock-error" class="error" hidden></p><button class="button" type="submit">Unlock in this tab</button></form>`;
  }
  if (!recoveryReady) return `<div class="empty">Your keys are unlocked, but sealed storage waits until the recovery kit is confirmed.</div>`;
  return `<form id="vault-form"><div class="field"><label for="vault-note">Private note</label><textarea id="vault-note" placeholder="Only an unlocked browser can read this…"></textarea></div><input id="vault-version" type="hidden" value="0" /><div class="button-row"><button class="button" type="submit">Seal note</button><span id="vault-status" class="hint"></span></div></form>`;
}

function grantTemplate(grant: DashboardView["grants"][number]): string {
  const hostname = new URL(grant.clientId).host;
  return `<div class="app-row"><div><div class="app-origin">${h(hostname)}</div><div class="scopes">${grant.scopes.map(scopeLabel).join(" · ")}</div></div><button class="button danger revoke" data-grant-id="${h(grant.id)}" type="button">Revoke</button></div>`;
}

function bindAuthForms(
  compact: boolean,
  onComplete?: () => Promise<void>,
  loginHint = "",
  expectedRootDid?: string,
  swedish = false,
): void {
  document.querySelectorAll<HTMLButtonElement>("[data-auth-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      authMode = button.dataset.authTab as "login" | "signup";
      const card = button.closest<HTMLElement>(".auth-card, .popup-card")!;
      card.innerHTML = authTemplate(compact, loginHint, swedish);
      bindAuthForms(compact, onComplete, loginHint, expectedRootDid, swedish);
    });
  });
  document.querySelector<HTMLFormElement>("#auth-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const errorElement = form.querySelector<HTMLElement>("#auth-error")!;
    setBusy(button, true, swedish ? "Ett ögonblick …" : authMode === "signup" ? "Creating keys…" : "Deriving keys…");
    errorElement.hidden = true;
    try {
      const values = new FormData(form);
      const identifier = String(values.get("identifier") ?? "");
      const password = String(values.get("password") ?? "");
      if (authMode === "signup") {
        const handle = String(values.get("handle") ?? "");
        const cryptoState = await createAccountCrypto(password, location.origin);
        await api("/api/auth/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            handle,
            email: identifier,
            displayName: String(values.get("displayName") ?? ""),
            authSecret: cryptoState.authSecret,
            rootDid: cryptoState.rootDid,
            publicJwk: cryptoState.publicJwk,
            kdf: cryptoState.kdf,
            wrappedRoot: cryptoState.wrappedRoot,
            wrappedDek: cryptoState.wrappedDek,
            descriptor: cryptoState.descriptor,
            ...(location.pathname === "/authorize" ? {entryApp:new URLSearchParams(location.search).get("client_id"),entryRedirect:new URLSearchParams(location.search).get("redirect_uri")} : {}),
          }),
        });
        unlocked = unlockedFromCreated(cryptoState);
      } else {
        const parameters = await api<{ kdf: KdfParameters }>("/api/auth/parameters", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier }),
        });
        const { authSecret } = await derivePasswordKeys(password, parameters.kdf);
        const signedIn = await api<{ rootDid: string; csrfToken: string }>("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier, authSecret }),
        });
        try {
          requireExpectedRootDid(expectedRootDid, signedIn.rootDid);
        } catch (error) {
          try {
            await api("/api/auth/logout", { method: "POST", headers: { "X-CSRF-Token": signedIn.csrfToken } });
          } catch { /* do not accept the mismatched identity even if logout confirmation fails */ }
          throw error;
        }
        if (!compact) await unlockWithPassword(password);
      }
      if (onComplete) await onComplete();
      else await renderHome();
    } catch (error) {
      errorElement.textContent = messageOf(error);
      errorElement.hidden = false;
      setBusy(button, false);
    }
  });
}

function bindDashboard(): void {
  if (dashboard && location.pathname === "/app-content") bindAppContent(dashboard.managedApps.find(item => item.id === new URLSearchParams(location.search).get("app")), appContentPage, dashboard.csrfToken, async () => { await renderHome(); toast("Ändringen har sparats."); });
  if (dashboard) bindEngagement(dashboard.csrfToken, dashboard.managedApps.map(item=>item.origin));
  if (dashboard) bindManagedApps(dashboard.csrfToken, async () => { await renderHome(); toast("Ändringen har sparats."); });
  document.querySelector<HTMLButtonElement>("[data-ui-identity-trigger]")?.addEventListener("click", () => {
    accountMenuOpen = !accountMenuOpen;
    rerenderDashboard();
    if (accountMenuOpen) document.querySelector<HTMLButtonElement>("[data-ui-switch-identity]")?.focus();
  });
  document.querySelector<HTMLButtonElement>("[data-ui-switch-identity]")?.addEventListener("click", () => void leaveDashboard());
  document.querySelectorAll<HTMLButtonElement>("[data-ui-remembered-identity]").forEach((button) => {
    button.addEventListener("click", () => void leaveDashboard(button.dataset.loginHint, button.dataset.rootDid));
  });
  document.querySelector<HTMLButtonElement>("[data-ui-logout]")?.addEventListener("click", () => void leaveDashboard());
  document.querySelector<HTMLButtonElement>("#copy-did")?.addEventListener("click", () => void copyDid());
  document.querySelector<HTMLButtonElement>("#copy-profile-did")?.addEventListener("click", () => void copyDid());
  document.querySelector<HTMLButtonElement>("#choose-avatar")?.addEventListener("click", () => document.querySelector<HTMLInputElement>("#avatar-input")?.click());
  document.querySelector<HTMLButtonElement>("#choose-cover")?.addEventListener("click", () => document.querySelector<HTMLInputElement>("#cover-input")?.click());
  document.querySelector<HTMLInputElement>("#avatar-input")?.addEventListener("change", (event) => void chooseProfileImage(event.currentTarget as HTMLInputElement, "avatar"));
  document.querySelector<HTMLInputElement>("#cover-input")?.addEventListener("change", (event) => void chooseProfileImage(event.currentTarget as HTMLInputElement, "cover"));
  document.querySelector<HTMLButtonElement>("#remove-avatar")?.addEventListener("click", (event) => void updateProfile({ avatarDataUri: null }, event.currentTarget as HTMLButtonElement));
  document.querySelector<HTMLButtonElement>("#remove-cover")?.addEventListener("click", (event) => void updateProfile({ coverDataUri: null }, event.currentTarget as HTMLButtonElement));
  document.querySelector<HTMLFormElement>("#profile-name-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    void updateProfile({ displayName: String(new FormData(form).get("displayName") ?? "") }, form.querySelector<HTMLButtonElement>("button[type=submit]")!);
  });
  document.querySelectorAll<HTMLButtonElement>(".open-setup").forEach((button) => {
    button.addEventListener("click", () => openSetupWizard(button.dataset.setupQuest as "recovery" | "openai"));
  });
  document.querySelector<HTMLFormElement>("#email-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!dashboard) return;
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const errorElement = form.querySelector<HTMLElement>("#email-error")!;
    const statusElement = form.querySelector<HTMLElement>("#email-status")!;
    const values = new FormData(form);
    setBusy(button, true, "Saving…");
    errorElement.hidden = true;
    statusElement.textContent = "";
    try {
      const parameters = await api<{ kdf: KdfParameters }>("/api/auth/parameters", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: dashboard.profile.handle }),
      });
      const { authSecret } = await derivePasswordKeys(String(values.get("password") ?? ""), parameters.kdf);
      const result = await api<{ email: string }>("/api/account/email", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken },
        body: JSON.stringify({ email: String(values.get("email") ?? ""), authSecret }),
      });
      dashboard.privateAccount.email = result.email;
      form.querySelector<HTMLInputElement>("#email-password")!.value = "";
      statusElement.textContent = "Saved. Use this email the next time you sign in.";
      button.dataset.label = "Change email";
      button.dataset.restoreHtml = "Change email";
      setBusy(button, false);
    } catch (error) {
      errorElement.textContent = messageOf(error);
      errorElement.hidden = false;
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLFormElement>("#console-email-attestation-start")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!dashboard?.privateAccount.email) return;
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const errorElement = form.querySelector<HTMLElement>("#console-credential-error")!;
    setBusy(button, true, unlocked ? "Sending..." : "Unlocking...");
    errorElement.hidden = true;
    try {
      if (!unlocked) {
        await unlockWithPassword(String(new FormData(form).get("password") ?? ""));
        setBusy(button, true, "Sending...");
      }
      pendingEmailVerification = await startVerifiedEmailAttestation(dashboard.privateAccount.email);
      rerenderDashboard();
    } catch (error) {
      errorElement.textContent = messageOf(error);
      errorElement.hidden = false;
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLFormElement>("#console-email-attestation-confirm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!dashboard || !pendingEmailVerification) return;
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const errorElement = form.querySelector<HTMLElement>("#console-credential-error")!;
    setBusy(button, true, "Verifying...");
    errorElement.hidden = true;
    try {
      const csrf = await currentCredentialWriteCsrf(pendingEmailVerification.subjectDid);
      const credential = await confirmVerifiedEmailAttestation(
        pendingEmailVerification.verificationId,
        String(new FormData(form).get("code") ?? ""),
      );
      await storeCredentialFromBrowser(credential, csrf);
      dashboard.csrfToken = csrf;
      pendingEmailVerification = null;
      toast("Verified email credential saved.");
      await renderHome();
    } catch (error) {
      errorElement.textContent = messageOf(error);
      errorElement.hidden = false;
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLButtonElement>("#cancel-console-email-attestation")?.addEventListener("click", () => {
    pendingEmailVerification = null;
    rerenderDashboard();
  });
  document.querySelector<HTMLFormElement>("#unlock-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button")!;
    const error = form.querySelector<HTMLElement>("#unlock-error")!;
    setBusy(button, true, "Unlocking…");
    try {
      await unlockWithPassword(String(new FormData(form).get("password") ?? ""));
      await renderHome();
    } catch (cause) {
      error.textContent = "That password could not open the sealed drawer.";
      error.hidden = false;
      setBusy(button, false);
    }
  });
  document.querySelectorAll<HTMLButtonElement>(".revoke").forEach((button) => {
    button.addEventListener("click", async () => {
      if (!dashboard || !confirm("Revoke this app now? Its current token will stop on the next request.")) return;
      await api(`/api/grants/${button.dataset.grantId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": dashboard.csrfToken },
      });
      toast("App access revoked.");
      await renderHome();
    });
  });
  document.querySelector<HTMLButtonElement>("#deactivate-openai")?.addEventListener("click", async () => {
    if (!dashboard || !confirm("Deactivate OpenAI embeddings and remove stored vectors from this node?")) return;
    await api("/api/services/openai.embeddings", {
      method: "DELETE",
      headers: { "X-CSRF-Token": dashboard.csrfToken },
    });
    toast("OpenAI embeddings deactivated.");
    await renderHome();
  });
  document.querySelector<HTMLButtonElement>("#connect-google-calendar")?.addEventListener("click", async () => {
    if (!dashboard) return;
    const result = await api<{ authorizationUrl: string }>("/api/services/google-calendar/oauth/start", {
      method: "POST",
      headers: { "X-CSRF-Token": dashboard.csrfToken },
    });
    location.assign(result.authorizationUrl);
  });
  document.querySelector<HTMLButtonElement>("#sync-google-calendar")?.addEventListener("click", async (event) => {
    if (!dashboard) return;
    const button = event.currentTarget as HTMLButtonElement;
    setBusy(button, true, "Refreshing…");
    try {
      dashboard.googleCalendar = await api<GoogleCalendarStatusView>("/api/services/google-calendar/sync", {
        method: "POST",
        headers: { "X-CSRF-Token": dashboard.csrfToken },
      });
      toast("Google Calendar copy refreshed.");
      rerenderDashboard();
    } catch (error) {
      toast(messageOf(error));
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLButtonElement>("#disconnect-google-calendar")?.addEventListener("click", async () => {
    if (!dashboard || !confirm("Disconnect Google Calendar and remove its synced copy from this Vibe identity?")) return;
    dashboard.googleCalendar = await api<GoogleCalendarStatusView>("/api/services/google-calendar", {
      method: "DELETE",
      headers: { "X-CSRF-Token": dashboard.csrfToken },
    });
    dashboard.googleCalendars = [];
    toast("Google Calendar disconnected.");
    rerenderDashboard();
  });
  document.querySelector<HTMLFormElement>("#google-calendar-selection")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!dashboard) return;
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const calendarIds = new FormData(form).getAll("calendarId").map(String);
    setBusy(button, true, "Saving…");
    try {
      const result = await api<{ status: GoogleCalendarStatusView; calendars: GoogleCalendarView[] }>(
        "/api/services/google-calendar/calendars",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken },
          body: JSON.stringify({ calendarIds }),
        },
      );
      dashboard.googleCalendar = result.status;
      dashboard.googleCalendars = result.calendars;
      toast("Calendar selection saved and refreshed.");
      rerenderDashboard();
    } catch (error) {
      toast(messageOf(error));
      setBusy(button, false);
    }
  });
  const vaultForm = document.querySelector<HTMLFormElement>("#vault-form");
  if (vaultForm) {
    void loadVaultNote();
    vaultForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!unlocked || !dashboard) return;
      const textarea = document.querySelector<HTMLTextAreaElement>("#vault-note")!;
      const version = Number(document.querySelector<HTMLInputElement>("#vault-version")!.value);
      const status = document.querySelector<HTMLElement>("#vault-status")!;
      try {
        const envelope = await encryptVaultValue(unlocked.dek, { text: textarea.value });
        const result = await api<{ version: number }>("/api/vault/first-note", {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken, "If-Match": `"${version}"` },
          body: JSON.stringify({ envelope }),
        });
        document.querySelector<HTMLInputElement>("#vault-version")!.value = String(result.version);
        status.textContent = "Encrypted in this tab and saved as ciphertext.";
      } catch (error) {
        status.textContent = messageOf(error);
      }
    });
  }
}

function rerenderDashboard(): void {
  if (!dashboard) return;
  app.innerHTML = dashboardTemplate(dashboard, dashboardPageFromPath(location.pathname));
  bindDashboard();
}

function rememberedCloudIdentities(): BrowserIdentity[] {
  try {
    return readBrowserIdentities(localStorage, browserIdentityStorageKey);
  } catch {
    return [];
  }
}

function rememberCloudIdentity(identity: BrowserIdentity): void {
  try {
    rememberBrowserIdentity(localStorage, browserIdentityStorageKey, identity);
  } catch (error) {
    if (error instanceof DOMException) return;
    throw error;
  }
}

async function leaveDashboard(loginHint?: string, expectedRootDid?: string): Promise<void> {
  if (!dashboard) return;
  try {
    await api("/api/auth/logout", { method: "POST", headers: { "X-CSRF-Token": dashboard.csrfToken } });
  } finally {
    unlocked?.dek.fill(0);
    unlocked = null;
    dashboard = null;
    accountMenuOpen = false;
    authMode = "login";
    app.innerHTML = landingTemplate();
    bindAuthForms(false, undefined, loginHint ?? "", expectedRootDid);
  }
}

async function chooseProfileImage(input: HTMLInputElement, kind: "avatar" | "cover"): Promise<void> {
  const file = input.files?.[0];
  if (!file) return;
  const button = document.querySelector<HTMLButtonElement>(kind === "avatar" ? "#choose-avatar" : "#choose-cover");
  try {
    if (button) setBusy(button, true, "Preparing…");
    const dataUri = await normalizeProfileImage(file, kind);
    await updateProfile(kind === "avatar" ? { avatarDataUri: dataUri } : { coverDataUri: dataUri }, button ?? undefined, false);
  } catch (error) {
    showProfileError(messageOf(error));
    if (button) setBusy(button, false);
  } finally {
    input.value = "";
  }
}

async function updateProfile(
  update: { displayName?: string; avatarDataUri?: string | null; coverDataUri?: string | null },
  button?: HTMLButtonElement,
  setButtonBusy = true,
): Promise<void> {
  if (!dashboard) return;
  if (button && setButtonBusy) setBusy(button, true, "Saving…");
  showProfileError("");
  try {
    const result = await api<{ profile: ProfileView }>("/api/account/profile", {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken },
      body: JSON.stringify(update),
    });
    rememberCloudIdentity(result.profile);
    dashboard.profile = result.profile;
    toast("Profile updated.");
    rerenderDashboard();
  } catch (error) {
    showProfileError(messageOf(error));
    if (button) setBusy(button, false);
  }
}

function showProfileError(message: string): void {
  const element = document.querySelector<HTMLElement>("#profile-error");
  if (!element) return;
  element.textContent = message;
  element.hidden = !message;
}

async function copyDid(): Promise<void> {
  if (!dashboard) return;
  await navigator.clipboard.writeText(dashboard.profile.rootDid);
  toast("DID copied.");
}

async function unlockWithPassword(password: string): Promise<void> {
  const envelopes = await api<{
    rootDid: string;
    kdf: KdfParameters;
    wrappedRoot: WrappedEnvelope;
    wrappedDek: WrappedEnvelope;
    descriptor: SignedDescriptorEvent;
  }>("/api/envelopes");
  const clear = await unlockEnvelopes(password, envelopes.kdf, envelopes.wrappedRoot, envelopes.wrappedDek);
  unlocked = { ...clear, rootDid: envelopes.rootDid, descriptor: envelopes.descriptor };
}

function openSetupWizard(quest: "recovery" | "openai"): void {
  if (!dashboard) return;
  clearSetupRefresh();
  const step = quest === "recovery"
    ? dashboard.recoveryReady ? 2 : pendingRecovery ? 2 : unlocked ? 1 : 0
    : dashboard.services[0]?.active ? 2 : 0;
  setupWizard = { quest, step };
  renderSetupWizard();
}

function renderSetupWizard(): void {
  if (!setupWizard || !dashboard) return;
  const root = document.querySelector<HTMLDivElement>("#modal-root")!;
  const recovery = setupWizard.quest === "recovery";
  const steps = recovery
    ? ["Unlock your keys", "Save the recovery kit", "Confirm separate copies"]
    : ["Understand provider use", "Verify an API key", "Index your notes"];
  const completed = recovery
    ? dashboard.recoveryReady ? 3 : pendingRecovery ? 2 : unlocked ? 1 : 0
    : dashboard.services[0]!.active ? 3 : Math.min(setupWizard.step, 1);
  root.innerHTML = `<div class="setup-backdrop"><section class="setup-wizard" role="dialog" aria-modal="true" aria-labelledby="setup-wizard-title">
    <button id="close-setup" class="setup-close" type="button" aria-label="Close setup">${icon("x", { size: 20 })}</button>
    ${setupRailTemplate(steps, setupWizard.step, completed)}
    <main class="setup-content">${recovery ? recoveryWizardStep(setupWizard.step) : openAIWizardStep(setupWizard.step)}</main>
    <aside class="setup-visual" aria-label="What this setup provides">${recovery ? recoveryVisualTemplate() : openAIVisualTemplate()}</aside>
  </section></div>`;
  bindSetupWizard(root, completed);
  if (!recovery && setupWizard.step === 2 && dashboard.services[0]!.pending > 0) scheduleSetupRefresh();
}

function setupRailTemplate(steps: string[], current: number, completed: number): string {
  const progress = Math.round((completed / steps.length) * 100);
  const guidedSteps = guidedStepsTemplate(steps.map((label, index) => ({
    label,
    state: index < completed ? "complete" as const : index === current ? "current" as const : "upcoming" as const,
  })));
  return `<aside class="setup-rail"><div><div class="eyebrow">Setup progress</div><h2 id="setup-wizard-title">${setupWizard?.quest === "recovery" ? "Protect recovery" : "Meaning search"}</h2></div>${guidedSteps}<div class="setup-progress"><div><strong>${completed} of ${steps.length}</strong><span>${progress}%</span></div><progress max="100" value="${progress}">${progress}%</progress><button id="skip-setup" class="button link" type="button">Finish later</button></div></aside>`;
}

function recoveryWizardStep(step: number): string {
  if (!dashboard) return "";
  if (step === 0) return `<div class="setup-breadcrumb"><span>1</span>Unlock your keys</div><h2>Open the sealed drawer in this tab.</h2><p class="setup-lede">Your password decrypts the root material locally so the browser can prepare a recovery copy. It is never sent to the node.</p>${unlocked ? `<div class="setup-success"><strong>Keys unlocked</strong><span>This tab can prepare the recovery kit.</span></div><button class="button wizard-next" type="button">Continue</button>` : `<form id="wizard-unlock-form"><input name="username" autocomplete="username" value="${h(dashboard.profile.handle)}" hidden /><div class="field"><label for="wizard-password">Password</label><input id="wizard-password" name="password" type="password" autocomplete="current-password" required /></div><p id="wizard-error" class="error" hidden></p><button class="button" type="submit">Unlock in this tab</button></form>`}`;
  if (step === 1) return `<div class="setup-breadcrumb"><span>2</span>Save the recovery kit</div><h2>Download one half of recovery.</h2><p class="setup-lede">The encrypted kit restores root authority only when combined with the separate code shown next. Store the file somewhere durable.</p><div class="setup-callout"><strong>The kit does not contain a data backup.</strong><span>Notes and app data need the separate portability work tracked in Slice F.</span></div>${pendingRecovery ? `<div class="setup-success"><strong>Kit downloaded</strong><span>Continue to record the separate code.</span></div><button class="button wizard-next" type="button">Continue</button>` : `<button id="download-recovery" class="button" type="button">Download recovery kit</button>`}`;
  if (dashboard.recoveryReady) return `<div class="setup-breadcrumb"><span>3</span>Recovery ready</div><div class="setup-finale"><span>${icon("circle-check", { size: 38 })}</span><h2>Your recovery authority is prepared.</h2><p>Keep the kit and written code in different places. A full data backup and restore drill are still required.</p><button id="finish-setup" class="button" type="button">Back to dashboard</button></div>`;
  const code = pendingRecovery?.code ?? "";
  return `<div class="setup-breadcrumb"><span>3</span>Confirm separate copies</div><h2>Write the code somewhere else.</h2><p class="setup-lede">Neither the downloaded kit nor this code can restore your root alone.</p><div class="recovery-code">${h(code)}</div><div class="warning-box"><strong>Kit + code equals your root authority.</strong><br>Do not keep them in the same folder or password manager entry.</div><form id="wizard-recovery-confirm"><div class="field"><label for="wizard-recovery-code">Type the code to confirm your copy</label><input id="wizard-recovery-code" autocomplete="off" spellcheck="false" required /></div><p id="wizard-error" class="error" hidden></p><button class="button" type="submit">Confirm recovery setup</button></form>`;
}

function openAIWizardStep(step: number): string {
  if (!dashboard) return "";
  const service = dashboard.services[0]!;
  if (step === 0) return `<div class="setup-breadcrumb"><span>1</span>Understand provider use</div><h2>Add meaning search without giving the app your key.</h2><p class="setup-lede">Your node sends declared note fields and search phrases to OpenAI for embeddings. VibeNotes receives ranked notes, never the provider credential.</p><div class="custody-list"><div><strong>What leaves</strong><span>Note bodies and meaning-search phrases.</span></div><div><strong>What stays hidden</strong><span>Your OpenAI API key is encrypted at rest and not returned to apps.</span></div><div><strong>Operator boundary</strong><span>The running node operator can technically decrypt this operational credential.</span></div></div><div class="button-row"><button class="button wizard-next" type="button">Continue</button><button id="skip-setup-inline" class="button link" type="button">Not now</button></div>`;
  if (step === 1) return `<div class="setup-breadcrumb"><span>2</span>Verify an API key</div><h2>${service.active ? "Replace the provider key." : "Connect OpenAI embeddings."}</h2><p class="setup-lede">Create a secret key in your OpenAI project, then return here and paste it below. The node verifies the key before saving the encrypted credential.</p><div class="provider-instructions"><span>${icon("key-round", { size: 22 })}</span><div><strong>Get the key from OpenAI</strong><ol><li>Open the API keys page and select the project Vibe should use.</li><li>Create a new secret key and copy it once.</li><li>Paste it below; Vibe verifies it before activation.</li></ol><a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">Open OpenAI API keys${icon("external-link", { size: 14 })}</a></div></div><form id="wizard-openai-form"><input name="username" autocomplete="username" value="${h(dashboard.profile.handle)}" hidden /><div class="field"><label for="wizard-openai-key">OpenAI API key</label><input id="wizard-openai-key" name="apiKey" type="password" autocomplete="off" required placeholder="sk-…" /><p class="field-note">The key goes only to your Vibe Cloud node, is verified with OpenAI, and is encrypted at rest.</p></div><p id="wizard-error" class="error" hidden></p><div class="button-row"><button class="button" type="submit">Verify & ${service.active ? "replace" : "activate"}</button><button class="button link wizard-back" type="button">Back</button></div></form>`;
  if (!service.active) return `<div class="setup-breadcrumb"><span>3</span>Index your notes</div><h2>Provider setup is not active.</h2><p class="setup-lede">Return to the key step when you are ready. Word search remains available without OpenAI.</p><button class="button wizard-back" type="button">Add an API key</button>`;
  return `<div class="setup-breadcrumb"><span>3</span>${service.pending > 0 ? "Indexing your notes" : "Meaning search ready"}</div><div class="setup-finale"><span>${icon(service.pending > 0 ? "sparkles" : "circle-check", { size: 38 })}</span><h2>${service.pending > 0 ? "Your node is building the index." : "Meaning search is ready."}</h2><p>Saving notes remains available while background work continues. Provider failures fall back to ordinary word search.</p><div class="service-details"><div><strong>${service.indexed}</strong><span>indexed</span></div><div><strong>${service.pending}</strong><span>pending</span></div>${service.failed ? `<div><strong>${service.failed}</strong><span>retrying</span></div>` : ""}</div><div class="button-row"><button id="finish-setup" class="button" type="button">Back to dashboard</button><button id="replace-openai" class="button secondary" type="button">Replace API key</button></div></div>`;
}

function recoveryVisualTemplate(): string {
  return `<div class="setup-visual-art recovery-art"><span>${icon("shield-check", { size: 46 })}</span><i>••••</i></div><div class="eyebrow">What you get</div><h3>Authority that survives one loss.</h3><ul><li>Encrypted recovery material</li><li>A separately stored recovery code</li><li>No server copy of the clear root key</li></ul>`;
}

function openAIVisualTemplate(): string {
  return `<div class="setup-visual-art service-art"><span>Notes</span><b>${icon("arrow-right", { size: 18 })}</b><span>Node</span><b>${icon("arrow-right", { size: 18 })}</b><span>OpenAI</span></div><div class="eyebrow">Delegated service</div><h3>The node performs the operation.</h3><ul><li>App never receives the provider key</li><li>Only declared note fields are indexed</li><li>Deactivate removes stored vectors</li></ul>`;
}

function bindSetupWizard(root: HTMLDivElement, completed: number): void {
  document.querySelector<HTMLElement>(".shell > .topbar")?.setAttribute("inert", "");
  document.querySelector<HTMLElement>(".shell > .page")?.setAttribute("inert", "");
  document.querySelector<HTMLElement>(".admin-sidebar")?.setAttribute("inert", "");
  document.querySelector<HTMLElement>(".admin-main")?.setAttribute("inert", "");
  const close = () => void closeSetupWizard();
  root.querySelector<HTMLButtonElement>("#close-setup")?.addEventListener("click", close);
  root.querySelector<HTMLButtonElement>("#skip-setup")?.addEventListener("click", close);
  root.querySelector<HTMLButtonElement>("#skip-setup-inline")?.addEventListener("click", close);
  root.querySelector<HTMLButtonElement>("#finish-setup")?.addEventListener("click", close);
  root.querySelectorAll<HTMLButtonElement>("[data-wizard-step]").forEach((button) => button.addEventListener("click", () => {
    if (!setupWizard) return;
    const step = Number(button.dataset.wizardStep);
    if (step <= Math.max(setupWizard.step, completed)) {
      setupWizard.step = step;
      renderSetupWizard();
    }
  }));
  root.querySelectorAll<HTMLButtonElement>(".wizard-next").forEach((button) => button.addEventListener("click", () => {
    if (!setupWizard) return;
    setupWizard.step = Math.min(2, setupWizard.step + 1);
    renderSetupWizard();
  }));
  root.querySelectorAll<HTMLButtonElement>(".wizard-back").forEach((button) => button.addEventListener("click", () => {
    if (!setupWizard) return;
    setupWizard.step = setupWizard.quest === "openai" && setupWizard.step === 2 ? 1 : Math.max(0, setupWizard.step - 1);
    renderSetupWizard();
  }));
  root.querySelector<HTMLButtonElement>("#replace-openai")?.addEventListener("click", () => {
    if (!setupWizard) return;
    setupWizard.step = 1;
    renderSetupWizard();
  });
  root.querySelector<HTMLFormElement>("#wizard-unlock-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const error = form.querySelector<HTMLElement>("#wizard-error")!;
    setBusy(button, true, "Unlocking…");
    try {
      await unlockWithPassword(String(new FormData(form).get("password") ?? ""));
      if (setupWizard) setupWizard.step = 1;
      renderSetupWizard();
    } catch {
      error.textContent = "That password could not open the sealed drawer.";
      error.hidden = false;
      setBusy(button, false);
    }
  });
  root.querySelector<HTMLButtonElement>("#download-recovery")?.addEventListener("click", async (event) => {
    if (!unlocked || !dashboard) return;
    const button = event.currentTarget as HTMLButtonElement;
    setBusy(button, true, "Preparing kit…");
    try {
      pendingRecovery = await makeRecoveryKit(unlocked);
      downloadJson(`vibe-recovery-${dashboard.profile.handle}.json`, pendingRecovery.kit);
      if (setupWizard) setupWizard.step = 2;
      renderSetupWizard();
    } catch (error) {
      toast(messageOf(error));
      setBusy(button, false);
    }
  });
  root.querySelector<HTMLFormElement>("#wizard-recovery-confirm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!pendingRecovery || !dashboard) return;
    const form = event.currentTarget as HTMLFormElement;
    const input = form.querySelector<HTMLInputElement>("#wizard-recovery-code")!;
    const error = form.querySelector<HTMLElement>("#wizard-error")!;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    if (normalizeCode(input.value) !== normalizeCode(pendingRecovery.code)) {
      error.textContent = "The code does not match. Check the written copy and try again.";
      error.hidden = false;
      return;
    }
    setBusy(button, true, "Confirming…");
    try {
      await api("/api/recovery/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken },
        body: JSON.stringify({
          recoveryKdf: pendingRecovery.recoveryKdf,
          wrappedRoot: pendingRecovery.wrappedRoot,
          wrappedDek: pendingRecovery.wrappedDek,
        }),
      });
      pendingRecovery = null;
      dashboard.recoveryReady = true;
      toast("Recovery kit confirmed. Your sealed drawer is ready.");
      renderSetupWizard();
    } catch (cause) {
      error.textContent = messageOf(cause);
      error.hidden = false;
      setBusy(button, false);
    }
  });
  root.querySelector<HTMLFormElement>("#wizard-openai-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!dashboard) return;
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const error = form.querySelector<HTMLElement>("#wizard-error")!;
    setBusy(button, true, "Verifying with OpenAI…");
    try {
      const status = await api<ServiceView>("/api/services/openai.embeddings", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": dashboard.csrfToken },
        body: JSON.stringify({ apiKey: String(new FormData(form).get("apiKey") ?? "") }),
      });
      dashboard.services[0] = status;
      if (setupWizard) setupWizard.step = 2;
      toast("OpenAI embeddings activated. Indexing has started.");
      renderSetupWizard();
    } catch (cause) {
      error.textContent = messageOf(cause);
      error.hidden = false;
      setBusy(button, false);
    }
  });
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...root.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  window.setTimeout(() => {
    root.querySelector<HTMLElement>(".setup-content input:not([hidden]), .setup-content button:not([disabled]), #close-setup")?.focus();
  });
}

async function closeSetupWizard(): Promise<void> {
  clearSetupRefresh();
  setupWizard = null;
  await renderHome();
}

function scheduleSetupRefresh(): void {
  clearSetupRefresh();
  setupRefreshTimer = window.setTimeout(async () => {
    if (!setupWizard || setupWizard.quest !== "openai" || setupWizard.step !== 2) return;
    try {
      dashboard = await api<DashboardView>("/api/dashboard");
      renderSetupWizard();
    } catch { /* the regular dashboard will surface session loss */ }
  }, 2_000);
}

function clearSetupRefresh(): void {
  if (setupRefreshTimer !== null) window.clearTimeout(setupRefreshTimer);
  setupRefreshTimer = null;
}

async function loadVaultNote(): Promise<void> {
  if (!unlocked) return;
  const response = await fetch("/api/vault/first-note");
  if (!response.ok) return;
  const item = (await response.json()) as { found?: boolean; envelope: WrappedEnvelope; version: number };
  if (item.found === false) return;
  const value = await decryptVaultValue<{ text: string }>(unlocked.dek, item.envelope);
  const textarea = document.querySelector<HTMLTextAreaElement>("#vault-note");
  const version = document.querySelector<HTMLInputElement>("#vault-version");
  if (textarea) textarea.value = value.text;
  if (version) version.value = String(item.version);
}

async function renderAuthorization(): Promise<void> {
  app.innerHTML = authorizationTemplate(undefined, `<div class="spinner"></div><p class="hint" style="text-align:center">Preparing sign-in…</p>`);
  try {
    const query = new URLSearchParams(location.search);
    const context = await api<AuthorizationContext>(`/api/oauth/context?${query.toString()}`);
    if (pendingAppOnboarding?.clientId !== context.app.origin) pendingAppOnboarding = null;
    if (pendingCredentialSelections?.clientId !== context.app.origin) pendingCredentialSelections = null;
    const loginHint = query.get("login_hint")?.slice(0, 254) ?? "";
    const localized = context.app.manifestVerified && context.app.presentation?.locale === "sv";
    authorizationAppName = context.app.name;
    if (localized) { document.title = `Logga in på ${context.app.name} · Vibe`; document.documentElement.lang = "sv"; }
    if (!context.authenticated) {
      app.innerHTML = authorizationTemplate(
        context.app,
        localized ? `<h2>Logga in på ${h(authorizationAppName)}</h2><p class="hint">${h(context.app.presentation?.description ?? "Fortsätt med ditt Vibe-konto.")}</p>${authTemplate(true, loginHint, true)}` : `<div class="eyebrow">Vibe account</div><h2>Sign in to ${h(context.app.name)}.</h2><p class="hint">Use your Vibe account to continue. ${h(context.app.name)} never sees your password.</p>${authTemplate(true, loginHint)}`,
      );
      bindAuthForms(true, renderAuthorization, loginHint, undefined, localized);
      return;
    }
    rememberCloudIdentity(context.profile!);
    if (query.get("prompt") === "login") {
      app.innerHTML = authorizationTemplate(
        context.app,
        localized ? `<h2>Fortsätt till ${h(authorizationAppName)}</h2><p class="hint">Välj det Vibe-konto du vill använda för appen.</p><div class="localized-identity"><strong>${h(context.profile?.displayName ?? "Ditt konto")}</strong><span>@${h(context.profile?.handle ?? "")}</span></div><div class="consent-actions"><button id="continue-current-account" class="button" type="button">Fortsätt som ${h(context.profile?.displayName ?? "inloggad")}</button><button id="confirm-account-switch" class="button secondary" type="button">Använd ett annat konto</button><button id="cancel-account-switch" class="button link" type="button">Avbryt</button></div>` : `<div class="eyebrow">Choose an identity</div><h2>Continue to ${h(context.app.name)}.</h2><p class="hint">Vibe Cloud is currently signed in as <strong>${h(context.profile?.displayName ?? "you")}</strong>${context.profile?.handle ? ` (@${h(context.profile.handle)})` : ""}. Choose deliberately whether to use it or sign in/create another identity.</p><div class="consent-actions"><button id="continue-current-account" class="button" type="button">Continue as ${h(context.profile?.displayName ?? "current identity")}</button><button id="confirm-account-switch" class="button secondary" type="button">Use another identity</button><button id="cancel-account-switch" class="button link" type="button">Cancel</button></div><p class="security-note">The app does not link these identities. Its remembered account list stays only in this browser.</p>`,
      );
      document.querySelector<HTMLButtonElement>("#continue-current-account")!.addEventListener("click", async (event) => {
        setBusy(event.currentTarget as HTMLButtonElement, true, "Continuing...");
        query.delete("prompt");
        history.replaceState({}, "", `${location.pathname}?${query.toString()}`);
        await renderAuthorization();
      });
      document.querySelector<HTMLButtonElement>("#confirm-account-switch")!.addEventListener("click", async (event) => {
        const button = event.currentTarget as HTMLButtonElement;
        setBusy(button, true, "Signing out…");
        try {
          await api("/api/auth/logout", { method: "POST", headers: { "X-CSRF-Token": context.csrfToken! } });
          query.delete("prompt");
          history.replaceState({}, "", `${location.pathname}?${query.toString()}`);
          authMode = "login";
          await renderAuthorization();
        } catch (error) {
          button.disabled = false;
          button.textContent = "Choose another identity";
          const note = document.querySelector<HTMLElement>(".security-note")!;
          note.className = "error";
          note.textContent = messageOf(error);
        }
      });
      document.querySelector<HTMLButtonElement>("#cancel-account-switch")!.addEventListener("click", () => window.close());
      return;
    }
    if (context.onboarding && !context.onboarding.complete && !pendingAppOnboarding) {
      renderAppOnboarding(context);
      return;
    }
    if (context.alreadyGranted) {
      app.innerHTML = authorizationTemplate(
        context.app,
        localized ? `<div class="spinner"></div><h3 style="text-align:center">Öppnar ${h(authorizationAppName)} …</h3><p class="security-note">Du har redan godkänt åtkomsten.</p>` : `<div class="spinner"></div><h3 style="text-align:center">Opening ${h(context.app.name)}…</h3><p class="security-note">Your existing permission is being reused.</p>`,
      );
      await submitConsent("existing", context.csrfToken!);
      return;
    }
    if (context.credentialRequirements && !pendingCredentialSelections) {
      renderCredentialRequirements(context);
      return;
    }
    renderAppConsent(context);
  } catch (error) {
    document.querySelector<HTMLElement>(".popup-card")!.innerHTML = `<h2>Could not continue</h2><p class="error">${h(messageOf(error))}</p><button id="close-popup" class="button secondary" type="button">Close</button>`;
    document.querySelector<HTMLButtonElement>("#close-popup")!.addEventListener("click", () => window.close());
  }
}

function renderAppOnboarding(context: AuthorizationContext): void {
  const onboarding = context.onboarding!;
  const values = pendingAppOnboarding?.values ?? onboarding.values;
  app.innerHTML = authorizationTemplate(context.app, `
    <div class="app-onboarding-progress" aria-label="Connection progress"><span class="done">${icon("circle-check", { size: 15 })} Vibe account</span><span class="current">1. App details</span><span>2. Permissions</span></div>
    <div class="eyebrow">Set up ${h(context.app.name)}</div>
    <h2>${h(onboarding.title)}</h2>
    <p class="hint">${h(onboarding.description)}</p>
    <form id="app-onboarding-form" class="app-onboarding-form">
      ${onboarding.fields.map((field) => appOnboardingFieldTemplate(field, values[field.id] ?? "")).join("")}
      <p id="app-onboarding-error" class="error" hidden></p>
      <div class="button-row"><button class="button" type="submit">Review permissions</button><button id="cancel-app-onboarding" class="button link" type="button">Not now</button></div>
    </form>
    <p class="security-note">These answers are stored for ${h(context.app.name)} only after you approve access. They are app-local, not part of your public Vibe profile or sealed drawer; the app and your node operator can read them.</p>
  `);
  document.querySelector<HTMLFormElement>("#app-onboarding-form")!.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const values = Object.fromEntries(context.onboarding!.fields.map((field) => [
      field.id,
      String(new FormData(form).get(field.id) ?? ""),
    ]));
    pendingAppOnboarding = { clientId: context.app.origin, values };
    await renderAuthorization();
  });
  document.querySelector<HTMLButtonElement>("#cancel-app-onboarding")!.addEventListener("click", async (event) => {
    setBusy(event.currentTarget as HTMLButtonElement, true, "Cancelling...");
    await submitConsent("deny", context.csrfToken!);
  });
}

function appOnboardingFieldTemplate(field: AppOnboardingFieldManifest, value: string): string {
  const note = `${field.description ? h(field.description) : ""}${field.description ? " · " : ""}${field.required ? "Required" : "Optional"}`;
  const common = `id="app-field-${field.id}" name="${field.id}" ${field.required ? "required" : ""} maxlength="${field.maxLength ?? (field.type === "textarea" ? 2000 : 200)}"`;
  let control: string;
  if (field.type === "textarea") {
    control = `<textarea ${common} rows="4" placeholder="${h(field.placeholder ?? "")}">${h(value)}</textarea>`;
  } else if (field.type === "select") {
    control = `<select ${common}><option value="">Choose an option</option>${(field.options ?? []).map((option) => `<option value="${h(option.value)}" ${option.value === value ? "selected" : ""}>${h(option.label)}</option>`).join("")}</select>`;
  } else {
    control = `<input ${common} type="${field.type}" value="${h(value)}" placeholder="${h(field.placeholder ?? "")}" autocomplete="off" />`;
  }
  return `<div class="field"><label for="app-field-${field.id}">${h(field.label)}${field.required ? "" : " <span>(optional)</span>"}</label>${control}<p class="field-note">${note}</p></div>`;
}

function renderCredentialRequirements(context: AuthorizationContext): void {
  const requirements = context.credentialRequirements ?? [];
  const missingRequired = requirements.some((requirement) => requirement.required && requirement.candidates.length === 0);
  const saved = pendingCredentialSelections?.clientId === context.app.origin
    ? pendingCredentialSelections.values
    : Object.fromEntries(requirements.flatMap((requirement) =>
      requirement.selectedCredentialId ? [[requirement.id, requirement.selectedCredentialId]] : []
    ));
  const requiredEmailMissing = requirements.some((requirement) =>
    requirement.required &&
    requirement.type === "vibe.credential.verified-email.v1" &&
    requirement.candidates.length === 0
  );
  app.innerHTML = authorizationTemplate(context.app, `
    <div class="app-onboarding-progress" aria-label="Connection progress"><span class="done">${icon("circle-check", { size: 15 })} Account</span><span class="current">Details</span><span>Review</span></div>
    <div class="eyebrow">Continue to ${h(context.app.name)}</div><h2>Choose the details to share.</h2>
    <p class="hint">Required details are marked. Optional details can be left out.</p>
    <form id="credential-selection-form" class="credential-requirements">
      ${requirements.map((requirement) => credentialRequirementTemplate(requirement, saved[requirement.id] ?? "", !missingRequired)).join("")}
      ${missingRequired ? "" : `<div class="button-row"><button class="button" type="submit">Review sharing</button><button id="cancel-credential-selection" class="button link" type="button">Not now</button></div>`}
    </form>
    ${requiredEmailMissing ? credentialEmailAcquisitionTemplate(context.credentialIssuers?.verifiedEmailAvailable === true) : ""}
    <p id="credential-flow-error" class="error" hidden></p>
    <details class="credential-security"><summary>How sharing works</summary><p>Vibe shares only the details you choose, never your whole wallet. Verified email is confirmed by Vibe Cloud; a name you add is signed by this identity. You can revoke app access later.</p></details>
  `);
  bindInlineNameUnlockFields();
  document.querySelector<HTMLFormElement>("#credential-selection-form")!.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const button = formElement.querySelector<HTMLButtonElement>("button[type=submit]")!;
    const form = new FormData(formElement);
    const errorElement = document.querySelector<HTMLElement>("#credential-flow-error")!;
    setBusy(button, true, "Preparing...");
    errorElement.hidden = true;
    try {
      const values = Object.fromEntries(requirements.map((requirement) => [
        requirement.id,
        String(form.get(requirement.id) ?? ""),
      ]));
      const inlineName = requirements.find((requirement) => canCreateInlineName(requirement));
      const rawName = inlineName ? String(form.get(`create-${inlineName.id}`) ?? "") : "";
      if (inlineName && rawName.trim()) {
        if (!unlocked) {
          const password = String(form.get(`create-${inlineName.id}-password`) ?? "");
          if (!password) throw new Error("Enter your Vibe password to save this name.");
          await unlockWithPassword(password);
        }
        const credential = await createSelfIssuedNameCredential(rawName);
        const csrf = await currentCredentialWriteCsrf(credential.subjectDid);
        await storeCredentialFromBrowser(credential, csrf);
        context.csrfToken = csrf;
        inlineName.candidates.unshift({
          id: credential.id,
          type: credential.type,
          issuerDid: credential.issuerDid,
          verification: "self_signed",
          disclosedClaims: { name: credential.claims.name! },
        });
        values[inlineName.id] = credential.id;
      }
      pendingCredentialSelections = { clientId: context.app.origin, values };
      renderAppConsent(context);
    } catch (error) {
      showCredentialFlowError(errorElement, error);
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLButtonElement>("#cancel-credential-selection")?.addEventListener("click", async (event) => {
    setBusy(event.currentTarget as HTMLButtonElement, true, "Cancelling...");
    await submitConsent("deny", context.csrfToken!);
  });
  bindCredentialAcquisition(context);
}

function credentialRequirementTemplate(requirement: CredentialRequirementView, selectedId: string, allowInlineCreate: boolean): string {
  const options = requirement.candidates.map((candidate) => {
    const claims = Object.entries(candidate.disclosedClaims)
      .map(([claim, value]) => `${credentialClaimLabel(claim)}: ${value}`)
      .join(", ");
    const selected = candidate.id === selectedId || (!selectedId && requirement.required && requirement.candidates.length === 1);
    return `<option value="${h(candidate.id)}" ${selected ? "selected" : ""}>${h(claims)} · ${candidate.verification === "issuer_signed" ? "Verified" : "Saved by you"}</option>`;
  }).join("");
  const state = requirement.candidates.length
    ? `<label class="credential-choice"><span>${requirement.required ? "Choose one" : "Share a saved name or skip"}</span><select name="${h(requirement.id)}" ${requirement.required ? "required" : ""}><option value="">${requirement.required ? "Choose" : "Don't share a name"}</option>${options}</select></label>`
    : canCreateInlineName(requirement) && allowInlineCreate
      ? inlineNameCreationTemplate(requirement)
      : `<div class="credential-missing ${requirement.required ? "required" : "optional"}">${requirement.required ? "Verify this to continue" : "Optional · you can leave this out"}</div>`;
  return `<section class="credential-requirement"><div class="credential-requirement-head"><strong>${h(requirement.label ?? requirement.claims.map(credentialClaimLabel).join(" + "))}</strong><span class="credential-necessity ${requirement.required ? "required" : "optional"}">${requirement.required ? "Required" : "Optional"}</span></div><p>${h(requirement.purpose)}</p>${state}</section>`;
}

function canCreateInlineName(requirement: CredentialRequirementView): boolean {
  return !requirement.required &&
    requirement.type === SELF_ISSUED_CREDENTIAL_TYPE &&
    requirement.claims.length === 1 &&
    requirement.claims[0] === "name" &&
    requirement.candidates.length === 0;
}

function inlineNameCreationTemplate(requirement: CredentialRequirementView): string {
  const unlock = unlocked ? "" : `<div class="credential-inline-unlock" data-name-unlock="${h(requirement.id)}" hidden><label for="credential-name-password-${h(requirement.id)}">Vibe password</label><input id="credential-name-password-${h(requirement.id)}" name="create-${h(requirement.id)}-password" type="password" autocomplete="current-password" /><small>Needed only to sign and save the name.</small></div>`;
  return `<div class="credential-inline-create"><label for="credential-name-${h(requirement.id)}">Name</label><input id="credential-name-${h(requirement.id)}" name="create-${h(requirement.id)}" data-inline-name="${h(requirement.id)}" autocomplete="name" maxlength="120" placeholder="Full or display name" />${unlock}<small>Enter a name to save and share it, or leave blank to skip.</small></div>`;
}

function bindInlineNameUnlockFields(): void {
  document.querySelectorAll<HTMLInputElement>("[data-inline-name]").forEach((input) => {
    const unlock = document.querySelector<HTMLElement>(`[data-name-unlock="${CSS.escape(input.dataset.inlineName!)}"]`);
    const password = unlock?.querySelector<HTMLInputElement>('input[type="password"]');
    if (!unlock || !password) return;
    const update = () => {
      const addingName = Boolean(input.value.trim());
      unlock.hidden = !addingName;
      password.required = addingName;
    };
    input.addEventListener("input", update);
    update();
  });
}

function credentialEmailAcquisitionTemplate(available: boolean): string {
  if (!available) {
    return `<div class="credential-acquisition"><strong>Email verification is unavailable</strong><p>This required detail cannot be confirmed right now. Try again later.</p></div>`;
  }
  if (!unlocked) {
    return `<form id="credential-unlock-form" class="credential-acquisition"><strong>Verify your email</strong><p>Enter your Vibe password to continue. It stays in this browser.</p><input name="username" autocomplete="username" hidden /><div class="field"><label for="credential-password">Vibe password</label><input id="credential-password" name="password" type="password" autocomplete="current-password" required /></div><button class="button secondary" type="submit">Unlock</button></form>`;
  }
  if (pendingEmailVerification) {
    return `<form id="credential-email-confirm-form" class="credential-acquisition"><strong>Check your email</strong><p>Enter the six-digit code sent to ${h(pendingEmailVerification.email)}.</p><div class="field"><label for="credential-email-code">Verification code</label><input id="credential-email-code" name="code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" required /></div><button class="button secondary" type="submit">Verify email</button></form>`;
  }
  return `<form id="credential-email-start-form" class="credential-acquisition"><strong>Verify your email</strong><p>We'll send a six-digit code to confirm the address.</p><div class="field"><label for="credential-email">Email</label><input id="credential-email" name="email" type="email" autocomplete="email" required /></div><button class="button secondary" type="submit">Send code</button></form>`;
}

async function startVerifiedEmailAttestation(emailInput: string): Promise<{ verificationId: string; email: string; subjectDid: string }> {
  if (!unlocked) throw new Error("Unlock this identity first.");
  const email = emailInput.normalize("NFKC").trim().toLowerCase();
  const payload = {
    version: 1 as const,
    subjectDid: unlocked.rootDid,
    email,
    requestedAt: new Date().toISOString(),
    nonce: randomBase64Url(24),
  };
  const request: EmailVerificationRequestV1 = {
    ...payload,
    proof: {
      algorithm: "ES256",
      encoding: "p1363",
      publicJwk: publicJwkFromPrivate(unlocked.privateJwk),
      value: await signWithUnlockedRoot(emailVerificationRequestSigningInput(payload)),
    },
  };
  const result = await api<{ verificationId: string; email: string }>(
    "/v1/attester/email-verifications",
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request) },
  );
  return { ...result, subjectDid: unlocked.rootDid };
}

async function confirmVerifiedEmailAttestation(
  verificationId: string,
  code: string,
): Promise<VerifiedEmailCredentialV1> {
  return api<VerifiedEmailCredentialV1>(
    `/v1/attester/email-verifications/${encodeURIComponent(verificationId)}/confirm`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) },
  );
}

function bindCredentialAcquisition(context: AuthorizationContext): void {
  const errorElement = document.querySelector<HTMLElement>("#credential-flow-error")!;
  document.querySelector<HTMLFormElement>("#credential-unlock-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const button = (event.currentTarget as HTMLFormElement).querySelector<HTMLButtonElement>("button")!;
    setBusy(button, true, "Unlocking...");
    try {
      await unlockWithPassword(String(new FormData(event.currentTarget as HTMLFormElement).get("password") ?? ""));
      renderCredentialRequirements(context);
    } catch (error) {
      showCredentialFlowError(errorElement, error);
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLFormElement>("#credential-email-start-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button")!;
    setBusy(button, true, "Sending...");
    try {
      pendingEmailVerification = await startVerifiedEmailAttestation(
        String(new FormData(form).get("email") ?? ""),
      );
      renderCredentialRequirements(context);
    } catch (error) {
      showCredentialFlowError(errorElement, error);
      setBusy(button, false);
    }
  });
  document.querySelector<HTMLFormElement>("#credential-email-confirm-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const button = form.querySelector<HTMLButtonElement>("button")!;
    setBusy(button, true, "Verifying...");
    try {
      const csrf = await currentCredentialWriteCsrf(pendingEmailVerification!.subjectDid);
      const credential = await confirmVerifiedEmailAttestation(
        pendingEmailVerification!.verificationId,
        String(new FormData(form).get("code") ?? ""),
      );
      await storeCredentialFromBrowser(credential, csrf);
      context.csrfToken = csrf;
      pendingEmailVerification = null;
      await renderAuthorization();
    } catch (error) {
      showCredentialFlowError(errorElement, error);
      setBusy(button, false);
    }
  });
}

async function createSelfIssuedNameCredential(nameInput: string): Promise<SelfIssuedCredentialV1> {
  if (!unlocked) throw new Error("Unlock this identity before saving a name.");
  const payload = {
    version: 1 as const,
    type: SELF_ISSUED_CREDENTIAL_TYPE,
    id: `self-${randomBase64Url(18)}`,
    issuerDid: unlocked.rootDid,
    subjectDid: unlocked.rootDid,
    claims: { name: normalizeSelfIssuedNameClaim(nameInput) },
    issuedAt: new Date().toISOString(),
  };
  return {
    ...payload,
    signature: {
      algorithm: "ES256",
      encoding: "p1363",
      keyId: `${unlocked.rootDid}#root`,
      value: await signWithUnlockedRoot(selfIssuedCredentialSigningInput(payload)),
    },
  };
}

async function signWithUnlockedRoot(input: string): Promise<string> {
  if (!unlocked) throw new Error("Unlock this identity first.");
  const privateKey = await crypto.subtle.importKey(
    "jwk",
    unlocked.privateJwk,
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-256" },
    privateKey,
    textEncoder.encode(input),
  );
  return toBase64Url(new Uint8Array(signature));
}

function publicJwkFromPrivate(privateJwk: JsonWebKey): JsonWebKey {
  return { kty: privateJwk.kty, crv: privateJwk.crv, x: privateJwk.x, y: privateJwk.y, ext: true };
}

async function storeCredentialFromBrowser(
  credential: SelfIssuedCredentialV1 | VerifiedEmailCredentialV1,
  csrf: string,
): Promise<void> {
  await api("/api/account/credentials", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify({ credential }),
  });
}

async function currentCredentialWriteCsrf(expectedRootDid: string): Promise<string> {
  const session = await api<SessionView>("/api/session");
  if (!session.authenticated || !session.csrfToken || session.profile?.rootDid !== expectedRootDid) {
    throw new Error("The active Vibe identity changed. Restart credential verification for the intended identity.");
  }
  return session.csrfToken;
}

function showCredentialFlowError(element: HTMLElement, error: unknown): void {
  element.hidden = false;
  element.textContent = messageOf(error);
}

function credentialClaimLabel(claim: string): string {
  if (claim === "email") return "Email";
  if (claim === "name") return "Name";
  if (claim === "nickname") return "Nickname";
  return claim.replaceAll("_", " ");
}

function renderAppConsent(context: AuthorizationContext): void {
  const localized = context.app.manifestVerified && context.app.presentation?.locale === "sv";
    authorizationAppName = context.app.name;
  const permissions = context.requestedScopes.map((scope): ReturnType<typeof permissionCopy> => localized && scope === "profile:read" ? { icon: "user-round", title: "Din profil", body: "Appen kan läsa ditt namn, användarnamn och din kontoidentitet." } : localized && scope === "storage" ? { icon: "database", title: "Appens data på ditt konto", body: "Appen får läsa och skriva i sitt eget utrymme på ditt konto. Dina andra appars data delas inte." } : permissionCopy(scope));
  const hasPendingOnboarding = pendingAppOnboarding?.clientId === context.app.origin;
  const hasCredentialRequirements = Boolean(context.credentialRequirements?.length);
  const credentialSelections = pendingCredentialSelections?.clientId === context.app.origin
    ? pendingCredentialSelections.values
    : {};
  const disclosed = (context.credentialRequirements ?? []).flatMap((requirement) => {
    const selected = requirement.candidates.find((candidate) => candidate.id === credentialSelections[requirement.id]);
    return selected ? [{ requirement, selected }] : [];
  });
  app.innerHTML = authorizationTemplate(context.app, `
    ${hasPendingOnboarding ? `<div class="app-onboarding-progress" aria-label="Connection progress"><span class="done">${icon("circle-check", { size: 15 })} Vibe account</span><span class="done">${icon("circle-check", { size: 15 })} App details</span><span class="current">2. Permissions</span></div>` : ""}
    ${localized ? `<h2>Koppla ditt konto till ${h(authorizationAppName)}</h2><p class="hint">Appen begär följande åtkomst.</p>` : `<div class="eyebrow">Connect ${h(context.app.name)}</div><h2>Continue to ${h(context.app.name)}.</h2>`}
    <div class="origin-box"><strong>${h(context.app.hostname)}${localized ? "" : ` · ${context.app.manifestVerified ? "Manifest registered" : "Unregistered origin"}`}</strong><span>${h(context.app.origin)}</span></div>
    <div class="permission-list">${permissions.map((permission) => `<div class="permission"><div class="permission-mark">${icon(permission.icon, { size: 21 })}</div><div><h3>${h(permission.title)}</h3><p>${h(permission.body)}</p></div></div>`).join("")}</div>
    ${disclosed.length ? `<div class="credential-consent-summary"><strong>Details you'll share</strong>${disclosed.map(({ selected }) => `<div><span>${h(Object.entries(selected.disclosedClaims).map(([claim, value]) => `${credentialClaimLabel(claim)}: ${value}`).join(", "))}</span><small>${selected.verification === "issuer_signed" ? "Verified by Vibe Cloud" : "Added and signed by you"}</small></div>`).join("")}</div>` : ""}
    ${hasPendingOnboarding ? `<button id="edit-app-onboarding" class="button link compact-link" type="button">Back to app details</button>` : ""}
    ${hasCredentialRequirements ? `<button id="edit-credential-selections" class="button link compact-link" type="button">Back to credentials</button>` : ""}
    <div class="consent-actions"><button class="button consent" data-decision="allow" type="button">${localized ? `Godkänn och fortsätt till ${h(context.app.name)}` : `Continue to ${h(context.app.name)}`}</button><button class="button secondary consent" data-decision="once" type="button">${localized ? "Bara den här gången" : "Just once"}</button><button class="button link deny consent" data-decision="deny" type="button">${localized ? "Avbryt" : "Don’t allow"}</button></div>
    <p class="security-note">${localized ? `Inloggad som ${h(context.profile?.displayName ?? "du")}. Du kan återkalla åtkomsten i Vibe Cloud.` : `Signed in as ${h(context.profile?.displayName ?? "you")} · Passwords belong only on ${h(location.host)}${hasPendingOnboarding ? ` · App details are saved when you continue` : ""}`}</p>`);
  document.querySelector<HTMLButtonElement>("#edit-app-onboarding")?.addEventListener("click", () => {
    renderAppOnboarding(context);
  });
  document.querySelector<HTMLButtonElement>("#edit-credential-selections")?.addEventListener("click", () => {
    renderCredentialRequirements(context);
  });
  document.querySelectorAll<HTMLButtonElement>(".consent").forEach((button) => button.addEventListener("click", async () => {
    document.querySelectorAll<HTMLButtonElement>(".consent").forEach((item) => { item.disabled = true; });
    await submitConsent(
      button.dataset.decision!,
      context.csrfToken!,
      pendingAppOnboarding?.values,
      pendingCredentialSelections?.values,
    );
  }));
}

async function renderGlobalLogout(): Promise<void> {
  const query = new URLSearchParams(location.search);
  const clientId = query.get("client_id") ?? "";
  const state = query.get("state") ?? "";
  app.innerHTML = authorizationTemplate(undefined, `<div class="spinner"></div><p class="hint" style="text-align:center">Checking your Vibe Cloud session...</p>`);
  try {
    if (state.length < 20) throw new Error("The logout request is missing secure state.");
    const context = await api<{
      authenticated: boolean;
      csrfToken?: string;
      profile?: { handle: string; displayName: string };
      app: { origin: string; name: string; hostname: string; manifestVerified: boolean };
    }>(`/api/auth/logout-context?client_id=${encodeURIComponent(clientId)}`);
    if (!context.authenticated) {
      finishGlobalLogout(context.app.origin, state);
      return;
    }
    app.innerHTML = authorizationTemplate(
      context.app,
      `<div class="eyebrow">Vibe Cloud session</div><h2>Log out of Vibe Cloud?</h2><p class="hint">This ends the current Vibe Cloud browser session for <strong>${h(context.profile?.displayName ?? "you")}</strong>${context.profile?.handle ? ` (@${h(context.profile.handle)})` : ""} and revokes ${h(context.app.name)} access for this identity. Other browser sessions are not affected.</p><div class="consent-actions"><button id="confirm-cloud-logout" class="button danger" type="button">Log out of Vibe Cloud</button><button id="cancel-cloud-logout" class="button secondary" type="button">Cancel</button></div><p class="security-note">This does not link identities or remove your Vibe Cloud account.</p>`,
    );
    document.querySelector<HTMLButtonElement>("#confirm-cloud-logout")!.addEventListener("click", async (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      setBusy(button, true, "Logging out...");
      try {
        await api("/api/auth/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": context.csrfToken! },
          body: JSON.stringify({ clientId: context.app.origin }),
        });
        finishGlobalLogout(context.app.origin, state);
      } catch (error) {
        button.disabled = false;
        button.textContent = "Log out of Vibe Cloud";
        const note = document.querySelector<HTMLElement>(".security-note")!;
        note.className = "error";
        note.textContent = messageOf(error);
      }
    });
    document.querySelector<HTMLButtonElement>("#cancel-cloud-logout")!.addEventListener("click", () => {
      if (window.opener) window.close(); else location.replace(context.app.origin);
    });
  } catch (error) {
    document.querySelector<HTMLElement>(".popup-card")!.innerHTML = `<h2>Could not log out</h2><p class="error">${h(messageOf(error))}</p><button id="close-logout" class="button secondary" type="button">Close</button>`;
    document.querySelector<HTMLButtonElement>("#close-logout")!.addEventListener("click", () => window.close());
  }
}

function finishGlobalLogout(clientOrigin: string, state: string): void {
  if (window.opener) {
    window.opener.postMessage({ type: "vibe:cloud-logout-complete", state }, clientOrigin);
    window.setTimeout(() => window.close(), 200);
    return;
  }
  location.replace(clientOrigin);
}

function setupBoardTemplate(view: DashboardView): string {
  const appConnected = view.grants.length > 0;
  const service = view.services[0]!;
  const done = Number(view.recoveryReady) + Number(appConnected) + Number(service.active);
  const app = view.availableApps[0];
  return `<section class="card span-12 setup-board">
    <div class="card-head"><div><div class="eyebrow">Setup quests</div><h3>Make this node yours</h3><p>Finish useful outcomes at your own pace. Each setup opens as a short, focused wizard.</p></div><span class="status blue">${done} of 3 complete</span></div>
    <div class="setup-quest-list">
      ${setupQuestRow({ icon: "shield-check", title: "Protect your recovery", summary: "Keep root authority recoverable if this browser or node is lost.", state: view.recoveryReady ? "done" : "current", action: `<button class="button secondary compact open-setup" data-setup-quest="recovery" type="button">${view.recoveryReady ? "Review" : "Start"}</button>` })}
      ${setupQuestRow({ icon: "external-link", title: "Connect your first app", summary: appConnected ? `${view.grants.length} app connection${view.grants.length === 1 ? "" : "s"} active.` : "Use your node from a separate web app without sharing your password.", state: appConnected ? "done" : "current", action: app ? `<a class="button secondary compact" href="${h(app.origin)}" target="_blank" rel="noopener noreferrer">${appConnected ? "Open app" : `Open ${h(app.name)}`}</a>` : "" })}
      ${setupQuestRow({ icon: "sparkles", title: "Enable meaning search", summary: service.active ? `${service.indexed} indexed · ${service.pending} pending.` : "Optional · Let VibeNotes find ideas that use different words.", state: service.active ? "done" : "optional", action: service.available ? `<button class="button secondary compact open-setup" data-setup-quest="openai" type="button">${service.active ? "Review" : "Set up"}</button>` : `<span class="hint">Node key required</span>` })}
    </div>
  </section>`;
}

function setupQuestRow(input: { icon: UiIconName; title: string; summary: string; state: "done" | "current" | "optional"; action: string }): string {
  const marker = icon(input.state === "done" ? "circle-check" : input.icon, { size: 20 });
  return `<div class="setup-quest ${input.state}"><span class="setup-quest-marker">${marker}</span><div><strong>${h(input.title)}</strong><p>${h(input.summary)}</p></div><div class="setup-quest-action">${input.action}</div></div>`;
}

function serviceTemplate(service: ServiceView): string {
  const state = service.active ? "Active" : service.available ? "Not connected" : "Node setup required";
  return `<div class="card-head"><div><h3>OpenAI embeddings</h3><p>A delegated service for meaning search. Apps can use the operation, never the API key.</p></div><span class="status ${service.active ? "good" : "warn"}"><span class="dot"></span>${state}</span></div>
    <div class="service-details"><div><strong>${h(service.model)}</strong><span>${service.dimensions} dimensions</span></div><div><strong>${service.indexed}</strong><span>indexed</span></div><div><strong>${service.pending}</strong><span>pending</span></div>${service.failed ? `<div><strong>${service.failed}</strong><span>retrying</span></div>` : ""}</div>
    ${service.lastError ? `<div class="warning-box"><strong>Recent provider error</strong><br>${h(service.lastError)}</div>` : ""}
    ${!service.available ? `<div class="empty">This node needs a server-side credential-encryption key before delegated services can be activated.</div>` : `<div class="button-row"><button class="button open-setup" data-setup-quest="openai" type="button">${service.active ? "Review setup" : "Set up meaning search"}</button>${service.active ? `<button id="deactivate-openai" class="button danger" type="button">Deactivate</button>` : ""}</div><p class="hint service-custody-summary">Setup explains what leaves the node, who can technically access the operational credential, and how the index is removed.</p>`}`;
}

function googleCalendarTemplate(status: GoogleCalendarStatusView, calendars: GoogleCalendarView[]): string {
  const state = status.connected ? "Connected" : status.needsReconnect ? "Reconnect required" : status.configured ? "Not connected" : "Operator setup required";
  const calendarRows = calendars.map((calendar) => `<label class="drawer"><input type="checkbox" name="calendarId" value="${h(calendar.externalId)}" ${calendar.included ? "checked" : ""} /><span class="drawer-icon">${icon("calendar-days", { size: 20 })}</span><div><strong>${h(calendar.summary)}</strong><p>${calendar.primary ? "Primary · " : ""}${h(calendar.timeZone ?? "Timezone not reported")}</p></div></label>`).join("");
  return `<div class="card-head"><div><h3>Google Calendar</h3><p>Copy selected calendars into this Vibe identity for read-only use. Apps never receive Google tokens.</p></div><span class="status ${status.connected ? "good" : "warn"}"><span class="dot"></span>${h(state)}</span></div>
    ${!status.configured ? `<div class="empty"><strong>Operator configuration required.</strong><br>The node needs an enabled Google OAuth client and encrypted service-credential storage before a connection can start.</div>` : !status.connected ? `<div class="button-row"><button id="connect-google-calendar" class="button" type="button">${status.needsReconnect ? "Reconnect Google Calendar" : "Connect Google Calendar"}</button></div><p class="hint">Vibe requests only calendar-list and event read access. Nothing can be created or changed in Google.</p>` : `<div class="service-details"><div><strong>${status.selectedCalendarCount}</strong><span>included calendars</span></div><div><strong>${status.eventCount}</strong><span>synced events</span></div><div><strong>${status.lastSyncAt ? h(new Date(status.lastSyncAt).toLocaleString()) : "Not yet"}</strong><span>last refresh</span></div></div>
      <form id="google-calendar-selection"><fieldset class="field"><legend>Calendars copied to Vibe</legend>${calendarRows || `<div class="empty">Google returned no calendars.</div>`}</fieldset><div class="button-row"><button class="button" type="submit">Save selection</button><button id="sync-google-calendar" class="button secondary" type="button">Refresh now</button><button id="disconnect-google-calendar" class="button danger" type="button">Disconnect</button></div></form>
      <p class="hint">Selection, sync cursors and the copied events stay inside this root DID's tenant. Disconnect removes the credential and copied calendar data.</p>`}
    ${status.lastError ? `<div class="warning-box"><strong>Recent sync issue</strong><br>${h(status.lastError)}</div>` : ""}`;
}

function authorizationTemplate(
  connectedApp: {
    name: string;
    manifestVerified?: boolean;
    presentation?: { tagline: string; description: string; logo?: string; heroImage?: string; locale?: string; accentColor?: string };
  } | undefined,
  card: string,
): string {
  if (!connectedApp) return `<main class="localized-auth-loading"><div class="card popup-card">${card}</div></main>`;
  if (connectedApp.manifestVerified && connectedApp.presentation?.locale) return `<main class="authorization-page localized-authorization" style="--accent:${/^#[a-fA-F0-9]{6}$/.test(connectedApp.presentation.accentColor ?? "") ? connectedApp.presentation.accentColor : "#1a73e8"}"><section class="authorization-panel"><div class="authorization-content"><div class="localized-auth-brand">${connectedApp.presentation.logo ? `<img src="${h(connectedApp.presentation.logo)}" alt="" width="48" height="48" />` : ""}<strong>${h(connectedApp.name)}</strong></div><div class="card popup-card">${card}</div><div class="authorization-provider"><span>${connectedApp.presentation.locale === "sv" ? "Kontohantering via" : "Account provided by"} <strong>Vibe</strong></span></div></div></section></main>`;
  const name = connectedApp?.name ?? "Vibe Cloud";
  const tagline = connectedApp?.presentation?.tagline ?? "Your apps. Your data. Your Vibe.";
  const description = connectedApp?.presentation?.description ?? "Sign in once and carry your identity and data between the apps you choose.";
  const logo = connectedApp?.presentation?.logo ?? "/assets/vibe-cloud.png";
  const heroImage = connectedApp?.presentation?.heroImage;
  const showcase = heroImage
    ? `<img class="showcase-hero" src="${h(heroImage)}" alt="" decoding="async" fetchpriority="high" referrerpolicy="no-referrer" />`
    : `<div class="showcase-copy"><div class="showcase-brand"><img src="${h(logo)}" alt="" /><span>${h(name)}</span></div><h1>${h(tagline)}</h1><p>${h(description)}</p></div><div class="showcase-notes" aria-hidden="true"><article>Book the cabin by the water this weekend.</article><article>A calmer home for ideas worth keeping.</article><article>Call Mum on Sunday.</article></div>`;
  return `<main class="authorization-page${heroImage ? " has-app-hero" : ""}">
    <section class="app-showcase">
      ${showcase}
    </section>
    <section class="authorization-panel"><div class="authorization-content"><div class="authorization-app-brand"><img src="${h(logo)}" alt="" referrerpolicy="no-referrer" /><div><strong>${h(name)}</strong><small>${h(tagline)}</small></div></div><div class="card popup-card">${card}</div><div class="authorization-provider"><img src="/assets/vibe-cloud.png" alt="" /><span>Secure sign-in by <strong>Vibe Cloud</strong></span></div></div></section>
  </main>`;
}

async function submitConsent(
  decision: string,
  csrf: string,
  onboarding?: Record<string, string>,
  credentialSelections?: Record<string, string>,
): Promise<void> {
  const query = new URLSearchParams(location.search);
  const result = await api<{ redirectTo: string }>("/api/oauth/decision", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
    body: JSON.stringify({
      decision,
      client_id: query.get("client_id"),
      redirect_uri: query.get("redirect_uri"),
      scope: query.get("scope"),
      state: query.get("state"),
      code_challenge: query.get("code_challenge"),
      ...(onboarding ? { onboarding } : {}),
      ...(credentialSelections ? { credentialSelections } : {}),
    }),
  });
  location.replace(result.redirectTo);
}

async function api<T>(path: string, init: RequestInit = {}, allowUnauthorized = false): Promise<T> {
  const response = await fetch(path, init);
  if (allowUnauthorized && response.status === 401) return { authenticated: false } as T;
  if (!response.ok) {
    let message = `Request failed (${response.status}).`;
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch { /* keep fallback */ }
    throw new Error(message.replaceAll("_", " "));
  }
  return (await response.json()) as T;
}

function unlockedFromCreated(created: AccountCrypto): UnlockedState {
  return { privateJwk: created.privateJwk, dek: created.dek, rootDid: created.rootDid, descriptor: created.descriptor };
}

function permissionCopy(scope: string): { icon: UiIconName; title: string; body: string } {
  if (scope === "support:write") return { icon: "shield-check", title: "Feedback och förbättringar", body: "Skicka feedback med ditt konto. Om du äger appen kan du också uttryckligen godkänna implementation och publicering av dina ärenden. Inloggning ger ingen ny roll." };
  if (scope === "app:content") return { icon: "database", title: "App content", body: "Manage shared content in this app. Requires an owner or administrator role; signing in alone grants no role." };
  if (scope === "profile:read") return { icon: "user-round", title: "See your basic profile", body: "Share your display name, handle, profile image, and public Vibe identity. Nothing from your sealed drawer." };
  if (scope === "onboarding:read") return { icon: "clipboard-check", title: "Use your app setup details", body: "Read only the answers this app declared and you reviewed during setup. They are app-local node-readable data, not part of your public profile or sealed drawer." };
  if (scope === "credentials.present") return { icon: "shield-check", title: "Receive approved credential claims", body: "Receive only the exact required or optional claims you select in this connection journey. The app never gets access to your wallet." };
  if (scope === `content.read:${POST_TEMPLATE_ID}`) return { icon: "file-text", title: "Read your posts and drafts", body: "Read only your Vibe Feed post records, including unpublished drafts. Other content types and app-private data stay unavailable." };
  if (scope === `content.write:${POST_TEMPLATE_ID}`) return { icon: "pencil", title: "Create and edit your posts", body: "Create revisions, change published state, and delete this exact post type. Every write is attributed to the Vibe Feed origin." };
  if (scope === "feed.publish") return { icon: "external-link", title: "Publish posts on the web", body: "Make posts you explicitly mark as published available without sign-in on your public Vibe Feed. Unpublished posts remain private to authorized apps." };
  if (scope.startsWith("content.read:")) return { icon: "file-text", title: "Read your notes", body: "Read node-readable content from this exact registered template. Nothing from your sealed drawer or other content types." };
  if (scope.startsWith("content.write:")) return { icon: "pencil", title: "Create and edit your notes", body: "Create revisions and deletion markers for this exact registered content type. Every write is attributed to this app origin." };
  if (scope === "collections.read") return { icon: "folder", title: "See your collections", body: "Read the collection names and note memberships stored on your node." };
  if (scope === "collections.write") return { icon: "arrow-left-right", title: "Organize your collections", body: "Create, rename, and remove collections, and change which notes belong to them." };
  if (scope === "calendar.read") return { icon: "calendar-days", title: "See your synced calendars", body: "Read only the calendars and events you chose to copy into this Vibe identity. Google credentials and unselected calendars stay with your node." };
  if (scope === "calendar.sync") return { icon: "refresh-cw", title: "Refresh your calendar copy", body: "Ask your node to pull the latest read-only changes from Google. This cannot create or change Google events." };
  if (scope.startsWith("semantic.search:")) return { icon: "search", title: "Search notes by meaning", body: "Use provider-generated vectors for this exact content type. The app receives ranked notes, not your OpenAI key." };
  if (scope === "service.use:openai.embeddings") return { icon: "sparkles", title: "Use OpenAI embeddings", body: "Send authorized note text and search phrases from your node to OpenAI for embedding. VibeNotes never receives the provider credential." };
  return { icon: "database", title: "Use its private app drawer", body: "Read and write VibeNotes data in a space isolated to this exact app origin. Your node operator can read this app data." };
}

function scopeLabel(scope: string): string {
  if (scope === "profile:read") return "Basic profile";
  if (scope === "onboarding:read") return "App setup details";
  if (scope === "storage") return "Private app storage";
  if (scope.startsWith("content.read:")) return `Read ${scope.slice("content.read:".length)}`;
  if (scope.startsWith("content.write:")) return `Write ${scope.slice("content.write:".length)}`;
  if (scope === "collections.read") return "Read collections";
  if (scope === "collections.write") return "Organize collections";
  if (scope === "calendar.read") return "Read synced calendars";
  if (scope === "calendar.sync") return "Refresh calendar copy";
  if (scope === "feed.publish") return "Publish public posts";
  if (scope.startsWith("semantic.search:")) return `Semantic search ${scope.slice("semantic.search:".length)}`;
  if (scope === "service.use:openai.embeddings") return "Use OpenAI embeddings";
  return scope;
}

function firstName(name: string): string { return name.trim().split(/\s+/u)[0] ?? name; }
function shortDid(did: string): string { return did.length > 28 ? `${did.slice(0, 18)}…${did.slice(-8)}` : did; }
function normalizeCode(value: string): string { return value.toUpperCase().replace(/[^A-Z2-9]/gu, ""); }
function messageOf(error: unknown): string { return error instanceof Error ? error.message : "Something went wrong."; }
function h(value: string): string { return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!); }

function setBusy(button: HTMLButtonElement, busy: boolean, label?: string): void {
  if (!button.dataset.label) button.dataset.label = button.textContent ?? "Continue";
  if (busy && !button.dataset.restoreHtml) button.dataset.restoreHtml = button.innerHTML;
  button.disabled = busy;
  if (busy) button.textContent = label ?? "Working…";
  else if (button.dataset.restoreHtml) {
    button.innerHTML = button.dataset.restoreHtml;
    delete button.dataset.restoreHtml;
  } else button.textContent = button.dataset.label;
}

function toast(message: string): void {
  toastElement.textContent = message;
  toastElement.classList.add("show");
  window.setTimeout(() => toastElement.classList.remove("show"), 3600);
}

function downloadJson(filename: string, value: unknown): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
